import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { Box, Divider, MenuItem, Menu, Stack, Tooltip, Typography, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Popover, IconButton } from "@mui/material";
import { DeleteOutline, EventAvailable, Schedule, InfoOutlined } from "@mui/icons-material";
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import BookingDialog from "./BookingDialog";
import AppointmentToolbar from "./AppointmentToolbar";
import ScheduleDialogCmp from "./ScheduleDialog";
import TimeOffDialogCmp from "./TimeOffDialog";
import MoveToDateDialogCmp from "./MoveToDateDialog";
import DayOverrideDialog from "./DayOverrideDialog";
import AppointmentChangeConfirmationDialog from "./AppointmentChangeConfirmationDialog";
import SendSmsDialog from "./SendSmsDialog";
import SendDayRemindersDialog from "./SendDayRemindersDialog";
import {
    toUtcIso,
    genId,
    fromLocalDateTime,
    minutesDiff,
    formatTimeLocal,
    addDays,
    sameDay,
    clamp,
    toLocalDateInputValue,
    parseLocalDateTimeString,
    toLocalDateTimeInputValue,
    timeOnly
} from "./utils";
import { differenceInMinutes } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useTheme, alpha } from "@mui/material/styles";
import { usePreferences } from "../../hooks/usePreferences";
import { ScheduleService } from "../../service/scheduleService";
import { EmployeeService } from "../../service/employeeService";
import { AppointmentService } from "../../service/appointmentService";
import { useEncounters } from '../../contexts/encountersContext';
import { useLocation } from '../../hooks/useLocation';
import Can from "components/auth/Can";
import { useCan } from "hooks/useCan";
import { toTitleCase } from "utils/index";

// Utilities only

// time off blocks per practitioner: { practitionerId, startUtcISO, endUtcISO, reason }

// utils moved to ./utils

// -------------------------
// Main Scheduler Component
// -------------------------
export default function Appointments() {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { preferences } = usePreferences();
    const tenantTimezone = preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const can = useCan()
    const canEditSchedule = can.section('scheduling', {action: 'create-schedule'})
    const canCreateAppointment = can.section('scheduling', {action: 'create-appointment'})
    const canSendText = can.section('scheduling', {action: 'send-appointment-text'})

    // Get global selected location
    const { selectedLocation } = useLocation();
    
    // Check if navigated from customer record with context
    const customerContext = useMemo(() => {
        const navigationState = window.history.state?.usr;
        return navigationState ? {
            customerId: navigationState.customerId,
            customerName: navigationState.customerName
        } : null;
    }, []);

    const StyledTooltip = useMemo(() => styled(({ className, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .MuiTooltip-tooltip`]: {
            background: `linear-gradient(135deg, ${theme.palette.grey[900]} 0%, ${theme.palette.grey[800]} 60%)`,
            color: theme.palette.grey[50],
            boxShadow: `0 4px 18px -2px rgba(0,0,0,0.4), 0 2px 6px -1px rgba(0,0,0,0.3)` ,
            borderRadius: 10,
            padding: '8px 10px',
            fontSize: 12,
            lineHeight: 1.15,
            backdropFilter: 'blur(6px)',
            border: `1px solid ${theme.palette.grey[700]}`,
            maxWidth: 260,
        },
        [`& .MuiTooltip-arrow`]: {
            color: theme.palette.grey[900]
        }
    })), []);

    const [infoAnchor, setInfoAnchor] = useState(null); // anchor element
    const [infoAppt, setInfoAppt] = useState(null); // selected appointment for popover
    const infoOpenTimerRef = useRef(null);
    const debounceOpenMs = 180; // prevent rapid flicker
    const openInfoDebounced = (el, appt) => {
        if (infoOpenTimerRef.current) clearTimeout(infoOpenTimerRef.current);
        infoOpenTimerRef.current = setTimeout(() => {
            setInfoAnchor(el);
            setInfoAppt(appt);
        }, debounceOpenMs);
    };
    const handleInfoClose = () => { if (infoOpenTimerRef.current) clearTimeout(infoOpenTimerRef.current); setInfoAnchor(null); setInfoAppt(null); };
    // View state
    const [view, setView] = useState("day"); // day | week | month
    const [activeDate, setActiveDate] = useState(() => new Date());

    // Filters - initialize locationId and location from global location selector
    const [locationId, setLocationId] = useState(() => selectedLocation?.id || '');
    const [location, setLocation] = useState(() => selectedLocation || null);
    const [selectedPractitionerIds, setSelectedPractitionerIds] = useState([]);
    const [encounterFilterId, setEncounterFilterId] = useState(null); // null => all
    const { encounters } = useEncounters();

    // Sync appointments location with global location selector changes
    // Only update if global location differs from current appointments location
    useEffect(() => {
        if (selectedLocation?.id && String(selectedLocation.id) !== String(locationId)) {
            setLocationId(selectedLocation.id);
            setLocation(selectedLocation);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation?.id]);

    // Schedules and time off (mock, editable)
    const [schedules, setSchedules] = useState(() => new Map()); // key `${pid}-${lid}` → weekly map fetched from backend
    const [timeOff, setTimeOff] = useState([]); // { id, practitionerId, startUtc, endUtc, reason }
    const [scheduleRefreshTick, setScheduleRefreshTick] = useState(0);

    // Per-date schedule overrides: key `${pid}-${lid}-${YYYY-MM-DD}` => { enabled: boolean, start?, end? }
    const [dayOverrides, setDayOverrides] = useState(() => {
        try {
            const raw = localStorage.getItem('scheduler-day-overrides');
            if (raw) {
                const obj = JSON.parse(raw);
                return new Map(Object.entries(obj));
            }
        } catch {}
        return new Map();
    });

    // Appointments loaded from backend
    const [appointments, setAppointments] = useState([]);
    // (Optional) could add loading/error indicators later

    // Helper to compute current fetch range based on view
    const computeRange = useCallback(() => {
        if (view === 'day') {
            const dayStr = toLocalDateInputValue(activeDate, tenantTimezone);
            return { start: dayStr, end: dayStr };
        } else if (view === 'week') {
            const weekStart = addDays(activeDate, -activeDate.getDay()); // Sunday
            const weekEnd = addDays(weekStart, 6);
            return { start: toLocalDateInputValue(weekStart, tenantTimezone), end: toLocalDateInputValue(weekEnd, tenantTimezone) };
        } else { // month view → fetch full visible month range (start Sunday before first, end Saturday after last)
            const first = new Date(activeDate.getFullYear(), activeDate.getMonth(), 1);
            const start = new Date(first);
            start.setDate(first.getDate() - first.getDay());
            const last = new Date(activeDate.getFullYear(), activeDate.getMonth() + 1, 0);
            const end = new Date(last);
            end.setDate(last.getDate() + (6 - last.getDay()));
            return { start: toLocalDateInputValue(start, tenantTimezone), end: toLocalDateInputValue(end, tenantTimezone) };
        }
    }, [view, activeDate]);

    const loadAppointments = useCallback(async () => {
        if (!locationId || !selectedPractitionerIds.length) { setAppointments([]); return; }
        const { start, end } = computeRange();
    // loading flags omitted for now
        try {
            // Backend only supports a single practitioner param; fetch per practitioner then merge
            const results = await Promise.all(selectedPractitionerIds.map(pid =>
                AppointmentService.get({ location: locationId, practitioner: pid, start, end })
                    .then(res => ({ pid, res }))
                    .catch(() => ({ pid, res: null }))
            ));
            const merged = [];
            results.forEach(({ res }) => {
                if (res && res.code === 0 && res.data && Array.isArray(res.data.booked)) {
                    res.data.booked.forEach(r => {
                        const cust = r.customer || {};
                        const rawName = cust.fullName || [cust.firstName, cust.middleName, cust.lastName].filter(Boolean).join(' ');
                        const name = (rawName || '').replace(/\s+/g,' ').trim();
                        const enc = r.Encounter || r.encounter || null;
                        merged.push({
                            id: r.id,
                            practitionerId: r.practitionerid || r.practitionerId,
                            locationId: r.locationid || r.locationId,
                            startUtc: r.startDate, // already ISO UTC per backend contract format
                            endUtc: r.endDate,
                            patientId: cust.id || null,
                            patientName: name,
                            patientAge: cust.age ?? null,
                            patientEmail: cust.email || '',
                            patientMobile: cust.mobile || '',
                            notes: r.notes || '',
                            status: r.status,
                            encounterId: enc?.id || r.encounterId || null,
                            encounterName: enc?.name || r.encounterName || null,
                            encounterDuration: enc?.duration || r.encounterDuration || null
                        });
                    });
                }
            });
            setAppointments(prev => merged);
        } catch (e) {
            // swallow; global interceptor toasts; keep existing appointments
        } finally {
            // done
        }
    }, [locationId, selectedPractitionerIds, computeRange]);

    // Initial + dependency-based loading
    useEffect(() => { loadAppointments(); }, [loadAppointments]);

    // Load time off for selected practitioners and current week
    useEffect(() => {
        let mounted = true;
        if (!locationId || !selectedPractitionerIds || selectedPractitionerIds.length === 0) return;
        // Start week on Sunday
        const weekStart = addDays(activeDate, -activeDate.getDay());
        const weekEnd = addDays(weekStart, 6);
        const startStr = toLocalDateInputValue(weekStart, tenantTimezone);
        const endStr = toLocalDateInputValue(weekEnd, tenantTimezone);
        (async () => {
            try {
                const res = await ScheduleService.listTimeOff({ practitioners: selectedPractitionerIds, start: startStr, end: endStr, locationId });
                if (!mounted) return;
                if (res?.code === 0 && Array.isArray(res.data)) {
                    // Normalize backend date strings (e.g., "yyyy-MM-dd HH:mm:ss" and occasional "24:00:00")
                    const normalizeLocalDateTime = (s) => parseLocalDateTimeString(s, tenantTimezone);
                    const mapped = res.data.map(r => ({
                        id: r.id,
                        practitionerId: r.EmployeeId || r.practitionerId,
                        startUtc: normalizeLocalDateTime(r.startDate),
                        endUtc: normalizeLocalDateTime(r.endDate),
                        reason: r.reason || ''
                    }));
                    setTimeOff(mapped);
                } else {
                    setTimeOff([]);
                }
            } catch {
                // errors globally handled
            }
        })();
        return () => { mounted = false; };
    }, [locationId, selectedPractitionerIds, activeDate]);

    // UI state
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [bookingDialog, setBookingDialog] = useState({ open: false, event: null, mode: "create" });
    const [scheduleDialog, setScheduleDialog] = useState({ open: false, practitionerId: null, locationId: null, mode: 'create' });
    const [timeOffDialog, setTimeOffDialog] = useState({ open: false, practitionerId: null, mode: 'create', timeOff: null });
    const [timeOffDeleteConfirmation, setTimeOffDeleteConfirmation] = useState({ open: false, timeOff: null });
    const [deletingTimeOff, setDeletingTimeOff] = useState(false);
    const [hoverY, setHoverY] = useState(null);
    const [contextMenu, setContextMenu] = useState({ anchorPosition: null, appt: null });
    const [moveToDate, setMoveToDate] = useState({ open: false, appt: null });
    const [actionsMenu, setActionsMenu] = useState({ anchorEl: null });
    const [dayOverrideDialog, setDayOverrideDialog] = useState({ open: false, practitionerId: null, locationId: null, date: null });
    // When multiple practitioners are selected, prompt to choose which one to edit
    const [practitionerPicker, setPractitionerPicker] = useState({ open: false, intent: null, date: null }); // intent: 'day-override' | 'series' | 'edit-schedule'
    const [sendSmsDialog, setSendSmsDialog] = useState({ open: false, appointment: null });
    const [confirmEditScope, setConfirmEditScope] = useState({ open: false, practitionerId: null, locationId: null, date: null });
    const [appointmentChangeConfirmation, setAppointmentChangeConfirmation] = useState({ open: false, change: null });
    const [appointmentChangeSaving, setAppointmentChangeSaving] = useState(false);
    const [dayRemindersDialog, setDayRemindersDialog] = useState({ open: false, date: null, locationId: null, locationName: null });
    const [monthOverflow, setMonthOverflow] = useState({ anchorEl: null, date: null });
    
    // Settings
    const [allowOverlapping, setAllowOverlapping] = useState(true); // Allow overlapping appointments by default

    // Show helpful banner when navigating from customer record (don't auto-open dialog)
    useEffect(() => {
        if (customerContext && customerContext.customerId) {
            showMsg(
                `Ready to book for ${customerContext.customerName}. Select location and practitioner to view availability.`,
                "info"
            );
        }
    }, [customerContext]);

    // Practitioners dictionary for names/colors
    const [practitionersMap, setPractitionersMap] = useState(() => new Map());
    useEffect(() => {
        let mounted = true;
        EmployeeService.getPractitioners()
            .then(res => {
                if (res && res.code === 0 && Array.isArray(res.data) && mounted) {
                    const m = new Map(res.data.map(p => [p.id, p]));
                    setPractitionersMap(m);
                }
            })
            .catch(() => {})
        return () => { mounted = false; };
    }, []);

    // Load weekly schedules for selected practitioners and current location from backend
    useEffect(() => {
        let mounted = true;
        // Require a location and at least one practitioner to fetch
        if (!locationId || !selectedPractitionerIds || selectedPractitionerIds.length === 0) return;
        // Compute current week range (Sun-Sat) based on activeDate
        const weekStart = addDays(activeDate, -activeDate.getDay());
        const weekEnd = addDays(weekStart, 6);
        const startStr = toLocalDateInputValue(weekStart, tenantTimezone);
        const endStr = toLocalDateInputValue(weekEnd, tenantTimezone);

        const load = async () => {
            try {
                const results = await Promise.all(
                    selectedPractitionerIds.map(pid =>
                        ScheduleService.mySchedule({ location: locationId, practitioner: pid, start: startStr, end: endStr })
                            .then(res => ({ pid, res }))
                            .catch(() => ({ pid, res: null }))
                    )
                );
                if (!mounted) return;
                setSchedules(prev => {
                    const map = new Map(prev);
                    results.forEach(({ pid, res }) => {
                        if (res && res.code === 0 && Array.isArray(res.data)) {
                            const rows = res.data;
                            const allNoRepeat = rows.length > 0 && rows.every(r => (r.repeatType ?? r.repeat_type) === 'norepeat');

                            const toDateStr = (raw) => {
                                if (!raw) return null;
                                if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                                    return raw;
                                }
                                return toLocalDateInputValue(new Date(raw), tenantTimezone);
                            };

                            // Compute the overall start and end dates for the weekly schedule based on the rows returned from the backend. This is used to set the _meta.start and _meta.end properties in the weekly schedule object.
                            const rowStartDates = rows
                                .map(r => toDateStr(r.startDate ?? r.start_date))
                                .filter(Boolean)
                                .sort();
                            const rowEndDates = rows
                                .map(r => toDateStr(r.endDate ?? r.end_date))
                                .filter(Boolean)
                                .sort();

                            const metaStart = rowStartDates[0] || startStr;
                            const metaEnd = rowEndDates[rowEndDates.length - 1] || endStr;

                            const weekly = {
                                _meta: {
                                    frequency: allNoRepeat ? 'norepeat' : 'weekly',
                                    end: { type: 'on', date: metaEnd }, // 
                                    start: metaStart, // earliest start date from rows or current week start
                                    nonRepeatEntries: []
                                }
                            };
                            rows.forEach(r => {
                                const dow = r.dayOfWeek ?? r.day_of_week;
                                const st = r.startTime ?? r.start_time;
                                const en = r.endTime ?? r.end_time;
                                if (allNoRepeat) {
                                    const rawDate = r.startDate ?? r.start_date;
                                    const date = toDateStr(rawDate);
                                    if (date && st && en) {
                                        weekly._meta.nonRepeatEntries.push({ date, start: st, end: en });
                                    }
                                    return;
                                }
                                if (dow !== undefined && st && en) {
                                    weekly[dow] = { enabled: true, start: st, end: en };
                                }
                            });
                            map.set(`${pid}-${locationId}`, weekly);
                        } else {
                            map.set(`${pid}-${locationId}`, null);
                        }
                    });
                    return map;
                });
            } catch (e) {
                // errors are globally toasted by interceptor; no-op here
            }
        };

        load();
        return () => { mounted = false; };
    }, [locationId, selectedPractitionerIds, activeDate, tenantTimezone, scheduleRefreshTick]);

    const handleMenuClose = () => {
        setContextMenu({ anchorPosition: null, appt: null });
    };
    const handleReassign = (newPid) => {
        const appt = appointments.find(x => x.id === contextMenu.appt?.id);
        if (!appt) { handleMenuClose(); return; }
        const ok = upsertRef.current({
            id: appt.id,
            practitionerId: newPid,
            locationId: appt.locationId,
            startUtc: appt.startUtc,
            endUtc: appt.endUtc,
            patientName: appt.patientName,
            notes: appt.notes
        });
        if (ok) showMsg("Appointment reassigned", "success");
        handleMenuClose();
    };

    // Derived values
    const openClose = useMemo(() => ({ start: "08:00", end: "20:00" }), []);
    // Build a lightweight practitioner list from selected IDs (placeholder names until real data wired)
    const practitionersShown = useMemo(() => (
        (selectedPractitionerIds || []).map(id => ({ id, name: practitionersMap.get(id)?.fullName || `#${id}` }))
    ), [selectedPractitionerIds, practitionersMap]);

    // Format MM/DD
    // formatMMDD moved to utils

    // Persist overrides
    useEffect(() => {
        try {
            const obj = Object.fromEntries(dayOverrides);
            //localStorage.setItem('scheduler-day-overrides', JSON.stringify(obj));
        } catch {}
    }, [dayOverrides]);

    // Helpers for schedule lookup
    const getWeeklyFor = useCallback((pid, lid) => {
        const key = `${pid}-${lid}`;
        return schedules.get(key) || null;
    }, [schedules]);
    const getDaySchedule = useCallback((date, pid, lid) => {
        const dayStr = toLocalDateInputValue(new Date(date), tenantTimezone);

        const oKey = `${pid}-${lid}-${dayStr}`;
        if (dayOverrides.has(oKey)) {
            return dayOverrides.get(oKey);
        }
        // Convert to tenant timezone to get correct day-of-week
        const zoned = utcToZonedTime(new Date(date), tenantTimezone);
        const dow = new Date(zoned).getDay();
        const wk = getWeeklyFor(pid, lid);

        if (!wk) return null;

        const meta = wk._meta || {};
        if (meta.start && dayStr < meta.start) {
            return null;
        }
        if (meta?.end?.type === 'on' && meta?.end?.date && dayStr > meta.end.date) {
            return null;
        }

        // Non-repeat schedules are date-specific; expose only matching dates.
        if (meta.frequency === 'norepeat') {
            const entries = Array.isArray(meta.nonRepeatEntries) ? meta.nonRepeatEntries : [];
            const dayEntries = entries.filter((entry) => entry?.date === dayStr && entry?.start && entry?.end);
            if (dayEntries.length === 0) return null;

            const sorted = [...dayEntries].sort((a, b) => a.start.localeCompare(b.start));
            const first = sorted[0];
            const last = sorted[sorted.length - 1];
            return {
                enabled: true,
                start: first.start,
                end: last.end,
            };
        }

        return wk[dow] || null;
    }, [getWeeklyFor, dayOverrides, tenantTimezone]);

    // Navigation
    const gotoToday = () => setActiveDate(new Date());
    const prev = () => {
        if (view === "day") setActiveDate(addDays(activeDate, -1));
        else if (view === "week") setActiveDate(addDays(activeDate, -7));
        else setActiveDate(new Date(activeDate.getFullYear(), activeDate.getMonth() - 1, 1));
    };
    const next = () => {
        if (view === "day") setActiveDate(addDays(activeDate, 1));
        else if (view === "week") setActiveDate(addDays(activeDate, 7));
        else setActiveDate(new Date(activeDate.getFullYear(), activeDate.getMonth() + 1, 1));
    };

    // Booking constraints
    const collidesTimeOff = useCallback((pid, startUtc, endUtc) => (
        timeOff.some(off => off.practitionerId === pid && !(new Date(endUtc) <= new Date(off.startUtc) || new Date(startUtc) >= new Date(off.endUtc)))
    ), [timeOff]);
    const insideSchedule = useCallback((date, pid, lid, startLocal, endLocal) => {
        const ds = getDaySchedule(date, pid, lid);
        
        // must have enabled schedule with valid start/end times
        if (!ds || !ds.enabled || !ds.start || !ds.end) {
            console.log('[insideSchedule] No valid schedule - returning false');
            return false;
        }
        
        const dayStart = fromLocalDateTime(date, ds.start, tenantTimezone);
        const dayEnd = fromLocalDateTime(date, ds.end, tenantTimezone);
        
        const startCheck = startLocal >= dayStart;
        const endCheck = endLocal <= dayEnd;
        const result = startCheck && endCheck;
        
        return result;
    }, [getDaySchedule, tenantTimezone]);

    const showMsg = (message, severity = "info") => setSnackbar({ open: true, message, severity });

    const deleteTimeOffEntry = async () => {
        const id = timeOffDeleteConfirmation.timeOff?.id;
        if (!id || deletingTimeOff) return;

        setDeletingTimeOff(true);
        try {
            await ScheduleService.deleteTimeOff(id);
            setTimeOff(prev => prev.filter(item => item.id !== id));
            setTimeOffDialog({ open: false, practitionerId: null, mode: 'create', timeOff: null });
            setTimeOffDeleteConfirmation({ open: false, timeOff: null });
            showMsg('Time off deleted', 'warning');
        } catch {
            // Error toast is handled by the HTTP interceptor; keep confirmation open for retry.
        } finally {
            setDeletingTimeOff(false);
        }
    };

    // Overlap detection with existing appointments
    const overlapsExisting = useCallback((pid, lid, startUtc, endUtc, exceptId) => {
        const s = new Date(startUtc), e = new Date(endUtc);
        return appointments.some(a => a.practitionerId === pid && a.locationId === lid && a.id !== exceptId && !(e <= new Date(a.startUtc) || s >= new Date(a.endUtc)));
    }, [appointments]);

    // Create or update appointment
    const upsertAppointment = useCallback((payload) => {
        const { id, practitionerId, locationId: lid } = payload;
        // Handle both old format (startUtc/endUtc) and new format (startdate/enddate as local strings)
        const startLocalStr = payload.startUtc || payload.startdate;
        const endLocalStr = payload.endUtc || payload.enddate;
        
        if (!startLocalStr || !endLocalStr) {
            showMsg("Invalid appointment times", "error");
            return false;
        }
        
        // Parse local datetime strings to Date objects for validation
        const startDate = typeof startLocalStr === 'string' && !startLocalStr.includes('Z') && !/[\+\-]\d{2}:\d{2}$/.test(startLocalStr)
            ? parseLocalDateTimeString(startLocalStr, tenantTimezone)
            : new Date(startLocalStr);
        const endDate = typeof endLocalStr === 'string' && !endLocalStr.includes('Z') && !/[\+\-]\d{2}:\d{2}$/.test(endLocalStr)
            ? parseLocalDateTimeString(endLocalStr, tenantTimezone)
            : new Date(endLocalStr);
        
        const dateLocal = startDate;
        
        if (collidesTimeOff(practitionerId, startDate, endDate)) { showMsg("Cannot book over time off", "warning"); return false; }
        if (!insideSchedule(dateLocal, practitionerId, lid, startDate, endDate)) { showMsg("Outside practitioner's schedule", "warning"); return false; }
        if (!allowOverlapping && overlapsExisting(practitionerId, lid, startDate, endDate, id)) { showMsg("Conflicts with another appointment", "warning"); return false; }

        if (id) {
            // Optimistic update
            setAppointments(prev => prev.map(a => a.id === id ? { ...a, ...payload, startUtc: startDate.toISOString(), endUtc: endDate.toISOString() } : a));
            AppointmentService.update({
                id,
                practitionerId,
                locationId: lid,
                startDate: startLocalStr,
                endDate: endLocalStr,
                notes: payload.notes || '',
                encounterId: payload.encounterId || null
            }).then(() => {
                showMsg('Appointment updated', 'success');
            }).catch(() => {
                showMsg('Failed to update – reverting', 'error');
                loadAppointments(); // revert by reloading
            });
        } else {
            // Create optimistic with temp id
            const tempId = `temp-${genId()}`;
            setAppointments(prev => [...prev, { ...payload, id: tempId, startUtc: startDate.toISOString(), endUtc: endDate.toISOString() }]);
            AppointmentService.bookAppointment({
                startdate: startLocalStr,
                enddate: endLocalStr,
                practitioner: practitionerId,
                location: lid,
                notes: payload.notes || null,
                customerId: payload.customerId || null,
                encounterId: payload.encounterId || null
            }).then(res => {
                if (res && res.code === 0 && res.data) {
                    const real = res.data;
                    setAppointments(prev => prev.map(a => a.id === tempId ? {
                        id: real.id,
                        practitionerId: real.practitionerid || practitionerId,
                        locationId: real.locationid || lid,
                        startUtc: real.startDate,
                        endUtc: real.endDate,
                        patientName: payload.patientName || '',
                        notes: real.notes || payload.notes || '',
                        encounterId: payload.encounterId || null,
                        encounterName: encounters.find(e => e.id === payload.encounterId)?.name || null,
                        encounterDuration: encounters.find(e => e.id === payload.encounterId)?.duration || null
                    } : a));
                    showMsg('Appointment booked', 'success');
                } else {
                    throw new Error('create failed');
                }
            }).catch(() => {
                showMsg('Failed to book appointment', 'error');
                setAppointments(prev => prev.filter(a => a.id !== tempId));
            });
        }
        return true;
    }, [allowOverlapping, collidesTimeOff, insideSchedule, overlapsExisting, loadAppointments, encounters]);

    const deleteAppointment = (id) => {
        // Optimistic update: remove from UI immediately
        setAppointments((prev) => prev.filter(a => a.id !== id));
        
        // Call backend to persist deletion
        AppointmentService.deleteAppointment(id)
            .then(() => {
                showMsg("Appointment deleted", "success");
            })
            .catch((error) => {
                showMsg("Failed to delete appointment", "error");
                // Reload appointments to restore state on error
                loadAppointments();
            });
    };

    const closeAppointmentChangeConfirmation = () => {
        if (appointmentChangeSaving) return;
        setAppointmentChangeConfirmation({ open: false, change: null });
    };

    const confirmAppointmentChange = async () => {
        const change = appointmentChangeConfirmation.change;
        if (!change || appointmentChangeSaving) return;

        const { original, pending } = change;
        const optimisticPending = {
            ...pending,
            startUtc: parseLocalDateTimeString(pending.startUtc, tenantTimezone).toISOString(),
            endUtc: parseLocalDateTimeString(pending.endUtc, tenantTimezone).toISOString()
        };
        setAppointmentChangeSaving(true);
        setAppointments(prev => prev.map(appointment => (
            appointment.id === original.id ? { ...appointment, ...optimisticPending } : appointment
        )));

        try {
            await AppointmentService.update({
                id: original.id,
                practitionerId: pending.practitionerId,
                locationId: pending.locationId,
                startDate: pending.startUtc,
                endDate: pending.endUtc,
                notes: original.notes || ''
            });
            showMsg('Appointment updated', 'success');
            setAppointmentChangeConfirmation({ open: false, change: null });
        } catch {
            showMsg('Failed to update – reverting', 'error');
            setAppointments(prev => prev.map(appointment => (
                appointment.id === original.id ? original : appointment
            )));
            setAppointmentChangeConfirmation({ open: false, change: null });
        } finally {
            setAppointmentChangeSaving(false);
        }
    };

    // Drag state
    const dragRef = useRef(null); // { id, originY, originStart, originEnd }
    const upsertRef = useRef(upsertAppointment);
    useEffect(() => { upsertRef.current = upsertAppointment; }, [upsertAppointment]);

    // Grid metrics
    const rowHeight = 28; // px per 30 minutes visual scale (15-min = rowHeight/2)
    const colHeaderHeight = 32;
    const defaultAppointmentMinutes = 15; // new appointment duration
    const minBlockMinutes = 15; // minimum visual block height

    // Color map per practitioner for month preview
    const practitionerColorMap = useMemo(() => {
        const mains = [
            theme.palette.primary.main,
            theme.palette.success.main,
            theme.palette.error.main,
            theme.palette.secondary.main,
            theme.palette.warning.main,
            theme.palette.info.main,
        ];
        const m = new Map();
        (selectedPractitionerIds || []).forEach((pid, idx) => {
            const main = mains[idx % mains.length] || theme.palette.primary.main;
            m.set(pid, {
                bg: alpha(main, 0.10),
                border: alpha(main, 0.35),
                dot: main
            });
        });
        return m;
    }, [theme, selectedPractitionerIds]);

    const getMinutesFromOpen = (clientY, gridTop, headerH = colHeaderHeight) => {
        const y = clientY - gridTop - headerH;
        // Each rowHeight is 30 minutes; 15 minutes is rowHeight/2
        const stepPx = rowHeight / 2;
        const mins = Math.round(y / stepPx) * 15;
        return clamp(mins, 0, 24 * 60);
    };

    // Appointment block positioning
    const computeBlockRect = (date, appt, ds, headerH = colHeaderHeight) => {
        // IMPORTANT: The grid background always starts at openClose.start (e.g., 08:00)
        // If a practitioner's schedule starts later (e.g., 10:00) and we base the block top
        // on ds.start, the visual offset becomes (appt.start - ds.start) instead of
        // (appt.start - openClose.start), shifting appointments upward by the difference.
        // This manifested as a consistent earlier placement (~2 hours) when schedules
        // started later than the global open time. We anchor to openClose.start to align
        // with the rendered row backgrounds and availability band positioning math.
        const open = fromLocalDateTime(date, openClose.start, tenantTimezone);
        const start = new Date(appt.startUtc);
        const end = new Date(appt.endUtc);
        const topMins = minutesDiff(start, open);
        const heightMins = Math.max(minBlockMinutes, minutesDiff(end, start));
        // Note: top is relative to the top of the cell content area (headers are outside the cell)
        return { top: (topMins / 30) * rowHeight, height: (heightMins / 30) * rowHeight };
    };

    // -------------------------
    // Booking Dialog
    // -------------------------
    // BookingDialog moved to ./BookingDialog

    // -------------------------
    // Schedule Dialog
    // -------------------------
    // ScheduleDialog moved to ./ScheduleDialog

    // -------------------------
    // Time Off Dialog
    // -------------------------
    // TimeOffDialog moved to ./TimeOffDialog

    // -------------------------
    // Move To Date Dialog
    // -------------------------
    // MoveToDateDialog moved to ./MoveToDateDialog

    // -------------------------
    // Top Toolbar
    // -------------------------
    // Toolbar extracted to ./AppointmentToolbar.js
    // -------------------------
    // Day/Week Grid
    // -------------------------
    const TimeGrid = ({ baseDate, days }) => {
        const containerRef = useRef(null);
        const hours = useMemo(() => {
            const start = openClose.start, end = openClose.end;
            const slots = [];
            const d0 = fromLocalDateTime(baseDate, start);
            const d1 = fromLocalDateTime(baseDate, end);
            let cur = new Date(d0);
            while (cur <= d1) {
                slots.push(new Date(cur));
                cur = new Date(cur.getTime() + 30 * 60000);
            }
            return slots;
        }, [baseDate]);

        const onGridClick = (e, dayIdx, pid) => {

            const rect = containerRef.current.getBoundingClientRect();
            const mins = getMinutesFromOpen(e.clientY, rect.top, headerHeight);
            const theDate = addDays(baseDate, dayIdx);
            const ds = getDaySchedule(theDate, pid, locationId);
            if (!ds || !ds.enabled) {
                showMsg("No schedule for this day", "warning");
                return;
            }
            const gridBase = fromLocalDateTime(theDate, openClose.start);
            const startLocal = new Date(gridBase.getTime() + mins * 60000);
            // Snap to nearest 15 minutes
            const stepMs = 15 * 60 * 1000;
            const start = new Date(Math.round(startLocal.getTime() / stepMs) * stepMs);
            const end = new Date(start.getTime() + defaultAppointmentMinutes * 60000);
            const event = {
                practitionerId: pid,
                locationId,
                startUtc: toUtcIso(start),
                endUtc: toUtcIso(end),
                patientName: customerContext?.customerName || "",
                customerId: customerContext?.customerId || null,
                notes: ""
            };
            // Only trigger when no active drag creation (fallback behavior)
            if (!dragRef.current) {
                setBookingDialog({ open: true, event, mode: "create" });
            }
        };

        const onMouseMove = (e) => {
            const rect = containerRef.current.getBoundingClientRect();
            setHoverY(e.clientY - rect.top);
        };
        const onMouseLeave = () => setHoverY(null);

        const headerHeight = days > 1 ? colHeaderHeight * 2 : colHeaderHeight;
        return (
            <Box ref={containerRef} sx={{ position: "relative", border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
                {/* Header Rows: Day labels (week) + Practitioner names */}
                <Stack direction="column" sx={{ position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper", borderBottom: "1px solid #eee" }}>
                    {/* Row 1: Day of week labels (shown for multi-day views) */}
                    <Stack direction="row" sx={{ display: days > 1 ? "flex" : "none" }}>
                        <Box sx={{ width: 64, height: colHeaderHeight }} />
                        {Array.from({ length: days }).map((_, dayIdx) => {
                            const d = addDays(baseDate, dayIdx);
                            const wd = d.toLocaleDateString([], { weekday: "short" });
                            const md = `${d.getMonth() + 1}/${d.getDate()}`;
                            const label = `${wd} ${md}`;
                            const clickable = days > 1; // week view
                            const handleDayHeaderClick = () => {
                                if (!canEditSchedule || !clickable) return;
                                if (practitionersShown.length !== 1) {
                                    showMsg("Select one practitioner, or click a practitioner name to edit", "info");
                                    return;
                                }
                                const pid = practitionersShown[0].id;
                                setConfirmEditScope({ open: true, practitionerId: pid, locationId, date: d });
                            };
                            return (
                                <Box 
                                    key={`daylabel-${dayIdx}`} 
                                    sx={{ flex: Math.max(1, practitionersShown.length), height: colHeaderHeight, display: "flex", alignItems: "center", justifyContent: "center", borderLeft: "1px solid #eee", cursor: clickable ? 'pointer' : 'default' }}
                                    onClick={handleDayHeaderClick}
                                    title={clickable ? 'Edit schedule (this date or series)' : ''}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{label}</Typography>
                                </Box>
                            );
                        })}
                    </Stack>
                    {/* Row 2: Practitioner names per day */}
                    <Stack direction="row">
                        <Box sx={{ width: 64, height: colHeaderHeight }} />
                        {Array.from({ length: days }).map((_, dayIdx) => (
                            <Stack key={`prrow-${dayIdx}`} direction="row" sx={{ flex: 1 }}>
                                {practitionersShown.map(p => {
                                    const d = addDays(baseDate, dayIdx);
                                    const clickable = days > 1; // week view
                                    const onClick = () => {
                                        if (!canEditSchedule || !clickable) return;
                                        setConfirmEditScope({ open: true, practitionerId: p.id, locationId, date: d });
                                    };
                                    return (
                                        <Box 
                                            key={`${dayIdx}-${p.id}`} 
                                            sx={{ flex: 1, height: colHeaderHeight, display: "flex", alignItems: "center", justifyContent: "center", borderLeft: "1px solid #eee", cursor: clickable ? 'pointer' : 'default' }}
                                            onClick={onClick}
                                            title={clickable ? 'Edit schedule (this date or series)' : ''}
                                        >
                                            <Typography variant="caption">{p.name}</Typography>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
                {/* Time Rows */}
                <Stack direction="row">
                    {/* time scale */}
                    <Box sx={{ width: 64, borderRight: "1px solid #eee" }}>
                        {hours.map((h, i) => (
                            <Box key={i} sx={{ height: rowHeight, borderBottom: "1px dashed #f0f0f0", fontSize: 10, color: "text.secondary", display: "flex", alignItems: "center", justifyContent: "flex-end", pr: 0.5 }}>
                                {i % 2 === 0 ? formatTimeLocal(h, preferences.timezone) : ""}
                            </Box>
                        ))}
                    </Box>
                    {/* days x practitioners grid */}
                    {Array.from({ length: days }).map((_, dayIdx) => (
                        <Stack key={dayIdx} direction="row" sx={{ flex: 1, borderLeft: "1px solid #eee" }}>
                            {practitionersShown.map(p => {
                                const date = addDays(baseDate, dayIdx);
                                const ds = getDaySchedule(date, p.id, locationId);
                                const openD = fromLocalDateTime(date, (ds?.start || openClose.start));
                                const closeD = fromLocalDateTime(date, (ds?.end || openClose.end));
                                const baseStart = fromLocalDateTime(date, openClose.start);
                                const availableOffset = ((differenceInMinutes(openD, baseStart)) / 30) * rowHeight;
                                const availableHeight = ((differenceInMinutes(closeD, openD)) / 30) * rowHeight;
                                return (
                                    <Box
                                        key={`${dayIdx}-${p.id}`}
                                        onMouseDown={(e) => {
                                            if (e.button !== 0) return; // left only
                                            // Ignore if clicking an appointment (they re-enable pointer events)
                                            if (e.target.closest('[data-appt-id]')) return;
                                            const rect = containerRef.current?.getBoundingClientRect();
                                            if (!rect) return;
                                            const localY = e.clientY - rect.top;
                                            const stepPx = rowHeight / 2; // 15m
                                            const mins = clamp(Math.round(((localY - headerHeight) / stepPx)) * 15, 0, 24 * 60);
                                            const date = addDays(baseDate, dayIdx);
                                            const dsNow = getDaySchedule(date, p.id, locationId);
                                            if (!dsNow || !dsNow.enabled) { showMsg('No schedule for this day', 'warning'); return; }
                                            const baseLocal = fromLocalDateTime(date, openClose.start, tenantTimezone);
                                            const start = new Date(baseLocal.getTime() + mins * 60000);
                                            dragRef.current = {
                                                id: null,
                                                create: true,
                                                activated: false,
                                                originMouseY: e.clientY,
                                                originCreateStart: start,
                                                originPid: p.id,
                                                originLocationId: locationId,
                                                originDayIdx: dayIdx,
                                                baseDate,
                                                days,
                                                practitionerIds: practitionersShown.map(pp => pp.id),
                                                containerLeft: rect.left,
                                                containerTop: rect.top,
                                                containerWidth: rect.width,
                                                headerHeight,
                                                openStart: openClose.start,
                                                weeklyByPid: Object.fromEntries(practitionersShown.map(pp => [pp.id, getWeeklyFor(pp.id, locationId)])),
                                                timeOffSnapshot: [...timeOff],
                                                apptsSnapshot: [...appointments]
                                            };
                                        }}
                                        onClick={(e) => onGridClick(e, dayIdx, p.id)}
                                        sx={{ flex: 1, position: "relative", borderLeft: "1px solid #fafafa", cursor: "crosshair" }}>
                                        {/* Time rows background */}
                                        {hours.map((_, i) => (
                                            <React.Fragment key={i}>
                                                <Box sx={{ position: "absolute", left: 0, right: 0, top: i * rowHeight, height: rowHeight, borderBottom: "1px solid #f0f0f0" }} />
                                                {/* 15-min mid line */}
                                                <Box sx={{ position: "absolute", left: 0, right: 0, top: i * rowHeight + rowHeight / 2, height: 0, borderTop: "1px dashed #f5f5f5" }} />
                                            </React.Fragment>
                                        ))}
                                        {/* Schedule visualization */}
                                        <Box sx={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, pointerEvents: "none" }}>
                                            {ds && ds.enabled ? (
                                                <>
                                                    {/* Available band */}
                                                    <Box sx={{ position: "absolute", left: 0, right: 0, top: availableOffset, height: availableHeight, bgcolor: "rgba(200,230,201,0.35)", borderTop: "1px solid #a5d6a7", borderBottom: "1px solid #a5d6a7" }} />
                                                    {/* Available label */}
                                                    <Typography 
                                                        variant="caption"
                                                        sx={{ 
                                                            position: "absolute", 
                                                            left: 6, 
                                                            top: availableOffset + 4, 
                                                            color: "#2e7d32", 
                                                            fontWeight: 700, 
                                                            bgcolor: "rgba(255,255,255,0.6)", 
                                                            px: 0.5, 
                                                            borderRadius: 0.5,
                                                            pointerEvents: 'auto',
                                                            cursor: days === 1 ? 'pointer' : 'default'
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (days === 1) {
                                                                setConfirmEditScope({ open: true, practitionerId: p.id, locationId, date });
                                                            }
                                                        }}
                                                        title={days === 1 ? 'Edit schedule (this date or series)' : ''}
                                                    >
                                                        {`Available ${formatTimeLocal(openD)} - ${formatTimeLocal(closeD)}`}
                                                    </Typography>
                                                    {/* before open */}
                                                    <Box sx={{ position: "absolute", left: 0, right: 0, top: 0, height: availableOffset, bgcolor: "#f8f8f8", opacity: 0.6 }} />
                                                    {/* after close */}
                                                    <Box sx={{ position: "absolute", left: 0, right: 0, top: availableOffset + availableHeight, bottom: 0, bgcolor: "#f8f8f8", opacity: 0.6 }} />
                                                </>
                                            ) : (
                                                <>
                                                    {/* Entire day OFF / No schedule */}
                                                    <Box sx={{ position: "absolute", inset: 0, bgcolor: "#eeeeee", opacity: 0.7 }} />
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ position: "absolute", top: 4, left: 6, color: "#9e9e9e", fontWeight: 600, pointerEvents: 'auto', cursor: days === 1 ? 'pointer' : 'default' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (days === 1) {
                                                                setConfirmEditScope({ open: true, practitionerId: p.id, locationId, date });
                                                            }
                                                        }}
                                                        title={days === 1 ? 'Edit schedule (this date or series)' : ''}
                                                    >
                                                        {ds ? "OFF" : "No schedule"}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                        {/* Time-off blocks */}
                                        {timeOff.filter(off => {
                                            if (off.practitionerId !== p.id) return false;
                                            
                                            const offStart = new Date(off.startUtc);
                                            const offEnd = new Date(off.endUtc);
                                            const dayStart = fromLocalDateTime(date, openClose.start);
                                            const dayEnd = fromLocalDateTime(date, openClose.end);
                                            // Render if time-off overlaps this day window at all
                                            return !(offEnd <= dayStart || offStart >= dayEnd);
                                        }).map(off => {
                                            // Clip to this day's visible range for correct positioning/height
                                            const dayStart = fromLocalDateTime(date, openClose.start);
                                            const dayEnd = fromLocalDateTime(date, openClose.end);
                                            const s = new Date(off.startUtc);
                                            const e = new Date(off.endUtc);
                                            const clippedStart = s < dayStart ? dayStart : s;
                                            const clippedEnd = e > dayEnd ? dayEnd : e;
                                            const rect = computeBlockRect(date, { startUtc: clippedStart, endUtc: clippedEnd }, { start: openClose.start }, headerHeight);
                                            return (
                                                <Box key={off.id} 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setTimeOffDialog({ open: true, practitionerId: p.id, mode: 'edit', timeOff: off });
                                                    }}
                                                    sx={{ position: "absolute", left: 2, right: 2, top: rect.top, height: rect.height, bgcolor: "#ffebee", border: "1px solid #ffcdd2", color: "#b71c1c", borderRadius: 0.5, p: 0.5, pointerEvents: "auto", cursor: 'pointer', fontSize: 12, overflow: 'hidden' }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>Time Off</Typography>
                                                    <Typography variant="caption" sx={{ display: "block" }}>{off.reason}</Typography>
                                                </Box>
                                            );
                                        })}
                                        {/* Drag ghost overlay (shows pending position without altering state when invalid) */}
                                        {(() => {
                                            const d = dragRef.current;
                                            const pending = d?.pending;
                                            if (d && d.create && !d.activated) return null; // suppress ghost pre-activation for creation
                                            if (d && d.resize && !d.activated) return null; // suppress for resize pre-activation
                                            // Check if we have a pending drag operation
                                            if (!pending) return null;
                                            const isTarget = pending.practitionerId === p.id && sameDay(pending.startUtc, date);
                                            if (!isTarget) return null;
                                            
                                            // Calculate ghost position considering overlaps
                                            const dayAppointments = appointments.filter(a => a.practitionerId === p.id && sameDay(a.startUtc, date) && a.id !== d.id && (!encounterFilterId || a.encounterId === encounterFilterId));
                                            const ghostItem = { ...pending, id: 'drag-ghost' };
                                            
                                            // Find overlapping appointments for the ghost
                                            const overlapping = dayAppointments.filter(a => {
                                                const ghostStart = new Date(pending.startUtc);
                                                const ghostEnd = new Date(pending.endUtc);
                                                const apptStart = new Date(a.startUtc);
                                                const apptEnd = new Date(a.endUtc);
                                                return !(ghostEnd <= apptStart || ghostStart >= apptEnd);
                                            });
                                            
                                            const totalColumns = overlapping.length + 1;
                                            const allOverlapping = [ghostItem, ...overlapping].sort((a, b) => new Date(a.startUtc) - new Date(b.startUtc));
                                            const ghostColumn = allOverlapping.findIndex(item => item.id === 'drag-ghost');
                                            
                                            const columnWidth = totalColumns > 1 ? (100 / totalColumns) : 100;
                                            const leftOffset = totalColumns > 1 ? (ghostColumn * columnWidth) : 0;
                                            
                                            const rect = computeBlockRect(date, { startUtc: pending.startUtc, endUtc: pending.endUtc }, ds || { start: openClose.start }, headerHeight);
                                            const invalid = d?.pendingValid === false;
                                            const meta = d?.pendingMeta || {};
                                            let title = '';
                                            if (invalid) {
                                                if (!meta.inside) {
                                                    title = "Outside practitioner's schedule";
                                                } else if (meta.offBlock) {
                                                    title = `Blocked by time off: ${formatTimeLocal(meta.offBlock.startUtc, preferences.timezone)} - ${formatTimeLocal(meta.offBlock.endUtc, preferences.timezone)}`;
                                                } else if (meta.conflictWith) {
                                                    const who = meta.conflictWith.patientName || "(no name)";
                                                    title = `Conflicts with ${who}: ${formatTimeLocal(meta.conflictWith.startUtc, preferences.timezone)} - ${formatTimeLocal(meta.conflictWith.endUtc, preferences.timezone)}`;
                                                } else {
                                                    title = "Unavailable";
                                                }
                                            } else {
                                                title = `${formatTimeLocal(pending.startUtc, preferences.timezone)} - ${formatTimeLocal(pending.endUtc, preferences.timezone)}`;
                                                if (overlapping.length > 0) {
                                                    title += ` (${overlapping.length} overlap${overlapping.length > 1 ? 's' : ''})`;
                                                }
                                            }
                                            const ghostSx = invalid
                                                ? { bgcolor: "transparent", border: "2px dashed #e53935" }
                                                : { 
                                                    bgcolor: totalColumns > 1 ? "rgba(255, 193, 7, 0.3)" : "#e3f2fd", 
                                                    border: totalColumns > 1 ? "2px dashed #ff9800" : "1px dashed #64b5f6", 
                                                    opacity: 0.8 
                                                };
                                            // Compute duration label
                                            const durMins = Math.max(1, Math.round((new Date(pending.endUtc) - new Date(pending.startUtc)) / 60000));
                                            const durLabel = durMins < 60 ? `${durMins}m` : `${Math.floor(durMins/60)}h${durMins%60 ? ' '+(durMins%60)+'m' : ''}`;
                                            const creating = !!d.create;
                                            return (
                                                <Tooltip title={title} placement="right" arrow open>
                                                    <Box sx={{ 
                                                        position: "absolute", 
                                                        left: `${leftOffset}%`, 
                                                        width: `${columnWidth}%`, 
                                                        top: rect.top, 
                                                        height: rect.height, 
                                                        borderRadius: 0.5, 
                                                        p: 0.5, 
                                                        pointerEvents: "none", 
                                                        zIndex: 4, 
                                                        display: 'flex', flexDirection: 'column', gap: 0.3,
                                                        ...ghostSx 
                                                    }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10 }}>
                                                                {creating ? 'Creating' : (d.resize ? 'Resizing' : 'Moving')}
                                                            </Typography>
                                                            <Box sx={{
                                                                fontSize: 10,
                                                                fontWeight: 700,
                                                                lineHeight: 1,
                                                                px: 0.6,
                                                                py: 0.2,
                                                                borderRadius: 10,
                                                                bgcolor: invalid ? '#ffcdd2' : (creating ? '#c8e6c9' : '#bbdefb'),
                                                                color: invalid ? '#b71c1c' : '#1b5e20'
                                                            }}>{durLabel}</Box>
                                                        </Box>
                                                        <Typography variant="caption" sx={{ fontSize: 10, display:'flex', gap:0.5, alignItems:'center', flexWrap:'wrap' }}>
                                                            <span>{formatTimeLocal(pending.startUtc, preferences.timezone)} - {formatTimeLocal(pending.endUtc, preferences.timezone)}</span>
                                                            {(!creating && !d.create && d.id && appointments.find(a => a.id === d.id)) && (
                                                                <span style={{ fontWeight:600, opacity:0.85 }}>· {(appointments.find(a => a.id === d.id)?.patientName || '(no name)')}</span>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            );
                                        })()}
                                        {/* Appointments for this practitioner and day */}
                                        {(() => {
                                            const dayAppointments = appointments.filter(a => a.practitionerId === p.id && sameDay(a.startUtc, date) && (!encounterFilterId || a.encounterId === encounterFilterId));
                                            const dragGhost = dragRef.current?.pending && 
                                                dragRef.current.pending.practitionerId === p.id && 
                                                sameDay(dragRef.current.pending.startUtc, date) ? 
                                                dragRef.current.pending : null;
                                            
                                            // Include drag ghost in overlap calculations
                                            const allItems = [...dayAppointments];
                                            if (dragGhost && !dayAppointments.find(a => a.id === dragRef.current?.id)) {
                                                allItems.push({ ...dragGhost, id: 'drag-ghost', isDragGhost: true });
                                            }
                                            
                                            // Calculate overlap groups and positions
                                            const calculateLayout = (items) => {
                                                //console.log('calculateLayout called with', items.length, 'items for date', date.toDateString(), 'practitioner', p.name);
                                                const layouts = items.map(item => {
                                                    const rect = computeBlockRect(date, item, ds || { start: openClose.start }, headerHeight);
                                                    return { ...item, rect, column: 0, maxColumns: 1 };
                                                });
                                                
                                                // Sort by start time
                                                layouts.sort((a, b) => new Date(a.startUtc) - new Date(b.startUtc));
                                                
                                                // Detect overlaps and assign columns
                                                for (let i = 0; i < layouts.length; i++) {
                                                    const current = layouts[i];
                                                    const overlapping = [];
                                                    
                                                    for (let j = 0; j < layouts.length; j++) {
                                                        if (i === j) continue;
                                                        const other = layouts[j];
                                                        
                                                        // Check for time overlap
                                                        const currentStart = new Date(current.startUtc);
                                                        const currentEnd = new Date(current.endUtc);
                                                        const otherStart = new Date(other.startUtc);
                                                        const otherEnd = new Date(other.endUtc);
                                                        
                                                        //console.log('  Comparing with appointment', other.id, 'from', otherStart.toLocaleTimeString(), 'to', otherEnd.toLocaleTimeString());
                                                        
                                                        if (!(currentEnd <= otherStart || currentStart >= otherEnd)) {
                                                            //console.log('    -> OVERLAP DETECTED!');
                                                            overlapping.push(other);
                                                        } else {
                                                            //console.log('    -> No overlap');
                                                        }
                                                    }
                                                    
                                                    if (overlapping.length > 0) {
                                                        //console.log('Found overlaps for appointment', current.id, ':', overlapping.map(o => o.id));
                                                        const totalColumns = overlapping.length + 1;
                                                        current.maxColumns = totalColumns;
                                                        
                                                        // Assign columns based on start time
                                                        const allOverlapping = [current, ...overlapping].sort((a, b) => new Date(a.startUtc) - new Date(b.startUtc));
                                                        allOverlapping.forEach((item, idx) => {
                                                            const layout = layouts.find(l => l.id === item.id);
                                                            if (layout) {
                                                                layout.column = idx;
                                                                layout.maxColumns = totalColumns;
                                                            }
                                                        });
                                                    }
                                                }
                                                
                                                return layouts;
                                            };
                                            
                                            const layouts = calculateLayout(allItems);
                                            
                                            return layouts.map(layout => {
                                                if (layout.isDragGhost) return null; // Ghost is handled separately
                                                
                                                const a = layout;
                                                const isDragging = dragRef.current?.id === a.id && !!dragRef.current?.pending;
                                                
                                                // Hide the original appointment when it's being dragged
                                                if (isDragging) return null;
                                                
                                                // Calculate position and width for overlapping appointments
                                                const columnWidth = layout.maxColumns > 1 ? (100 / layout.maxColumns) : 100;
                                                const leftOffset = layout.maxColumns > 1 ? (layout.column * columnWidth) : 0;
                                                
                                                const onMouseDown = (e) => {
                                                    e.stopPropagation();
                                                    const containerRect = containerRef.current?.getBoundingClientRect();
                                                    if (!containerRect) return;
                                                    const weeklyByPid = Object.fromEntries(practitionersShown.map(pp => [pp.id, getWeeklyFor(pp.id, locationId)]));
                                                    dragRef.current = {
                                                        id: a.id,
                                                        originMouseY: e.clientY,
                                                        activated: false,
                                                        originStart: new Date(a.startUtc),
                                                        originEnd: new Date(a.endUtc),
                                                        originPid: p.id,
                                                        originLocationId: locationId,
                                                        originPatientName: a.patientName,
                                                        originNotes: a.notes,
                                                        originDayIdx: dayIdx,
                                                        baseDate,
                                                        days,
                                                        practitionerIds: practitionersShown.map(pp => pp.id),
                                                        containerLeft: containerRect.left,
                                                        containerTop: containerRect.top,
                                                        containerWidth: containerRect.width,
                                                        headerHeight,
                                                        openStart: openClose.start,
                                                        weeklyByPid,
                                                        timeOffSnapshot: [...timeOff],
                                                        apptsSnapshot: [...appointments]
                                                    };
                                                };
                                                const onContextMenu = (e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setContextMenu({ anchorPosition: { top: e.clientY, left: e.clientX }, appt: a });
                                                };
                                                const onClick = (e) => {
                                                    e.stopPropagation();
                                                    // Only show menu if drag was never activated (stayed within dead-zone)
                                                    if (!dragRef.current?.activated) {
                                                        setContextMenu({ anchorPosition: { top: e.clientY, left: e.clientX }, appt: a });
                                                    }
                                                };
                                                
                                                // Status-based colors
                                                const getStatusColors = (status, isOverlapping) => {
                                                    switch(status) {
                                                        case 'confirmed':
                                                            return {
                                                                bgcolor: isOverlapping ? '#b8f4be' : '#21d739',
                                                                border: isOverlapping ? '1px solid #4caf50' : '1px solid #1db832',
                                                                hoverBg: isOverlapping ? '#a5e8ab' : '#1ec936',
                                                                hoverBorder: isOverlapping ? '#43a047' : '#1aad2d'
                                                            };
                                                        case 'arrived':
                                                            return {
                                                                bgcolor: isOverlapping ? '#e1bee7' : '#ce93d8',
                                                                border: isOverlapping ? '1px solid #ba68c8' : '1px solid #ab47bc',
                                                                hoverBg: isOverlapping ? '#d8aee1' : '#c084cf',
                                                                hoverBorder: isOverlapping ? '#ab47bc' : '#9c27b0'
                                                            };
                                                        case 'cancelled':
                                                            return {
                                                                bgcolor: isOverlapping ? '#ffcdd2' : '#ef9a9a',
                                                                border: isOverlapping ? '1px solid #e57373' : '1px solid #ef5350',
                                                                hoverBg: isOverlapping ? '#ffb9bf' : '#e88c8f',
                                                                hoverBorder: isOverlapping ? '#ef5350' : '#e53935'
                                                            };
                                                        case 'completed':
                                                            return {
                                                                bgcolor: isOverlapping ? '#d7ccc8' : '#bcaaa4',
                                                                border: isOverlapping ? '1px solid #a1887f' : '1px solid #8d6e63',
                                                                hoverBg: isOverlapping ? '#cdbfbb' : '#b09a94',
                                                                hoverBorder: isOverlapping ? '#8d6e63' : '#795548'
                                                            };
                                                        case 'noshow':
                                                            return {
                                                                bgcolor: isOverlapping ? '#ffe0b2' : '#ffcc80',
                                                                border: isOverlapping ? '1px solid #ffb74d' : '1px solid #ffa726',
                                                                hoverBg: isOverlapping ? '#ffd4a3' : '#ffbd6b',
                                                                hoverBorder: isOverlapping ? '#ffa726' : '#ff9800'
                                                            };
                                                        case 'paid':
                                                            return {
                                                                bgcolor: isOverlapping ? '#c5e1a5' : '#aed581',
                                                                border: isOverlapping ? '1px solid #9ccc65' : '1px solid #8bc34a',
                                                                hoverBg: isOverlapping ? '#b8d898' : '#a0ca7a',
                                                                hoverBorder: isOverlapping ? '#8bc34a' : '#7cb342'
                                                            };
                                                        default: // 'booked' and others
                                                            return {
                                                                bgcolor: isOverlapping ? '#fff3e0' : '#e3f2fd',
                                                                border: isOverlapping ? '1px solid #ff9800' : '1px solid #90caf9',
                                                                hoverBg: isOverlapping ? '#ffe0b2' : '#bbdefb',
                                                                hoverBorder: isOverlapping ? '#f57c00' : '#1e88e5'
                                                            };
                                                    }
                                                };
                                                
                                                const statusColors = getStatusColors(a.status, layout.maxColumns > 1);
                                                
                                                // Build tooltip content for patient/contact summary
                                                const tooltipLines = [];
                                                const ageFrag = a.patientAge != null ? ` (${a.patientAge} yrs)` : '';
                                                tooltipLines.push(`${toTitleCase(a.patientName) || '(no name)'}${ageFrag}`);
                                                const encounterLine = a.encounterName ? `${a.encounterName}${a.encounterDuration ? ` (${a.encounterDuration}m)` : ''}` : '';
                                                tooltipLines.push(`${timeOnly(a.startUtc, preferences)} - ${timeOnly(a.endUtc, preferences)}`);
                                                if (encounterLine) tooltipLines.push(`Encounter: ${a.encounterName}${a.encounterDuration ? ` (${a.encounterDuration}m)` : ''}`);
                                                if (a.patientEmail) tooltipLines.push(a.patientEmail);
                                                if (a.patientMobile) tooltipLines.push(a.patientMobile);
                                                const tooltipTitle = tooltipLines.join('\n');
                                                return (
                                                    isMobile ? (
                                                        <Box key={`${a.id}-${a.startUtc}`}
                                                            onMouseDown={onMouseDown}
                                                            onClick={onClick}
                                                            onContextMenu={onContextMenu}
                                                            sx={{ position: "absolute", left: `${leftOffset}%`, width: `${columnWidth}%`, top: layout.rect.top, height: layout.rect.height, zIndex: layout.maxColumns > 1 ? 3 : 2, bgcolor: statusColors.bgcolor, border: statusColors.border, borderRadius: 0.5, p: 0.3, cursor: "grab", userSelect: "none", fontSize: 11, transition: "box-shadow 120ms, border-color 120ms, background-color 120ms", '&:hover': { boxShadow: 2, borderColor: statusColors.hoverBorder, bgcolor: statusColors.hoverBg } }}
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                sx={{ position:'absolute', top:2, right:2, p:0.3, bgcolor:'rgba(0,0,0,0.25)', color:'#fff', '&:hover': { bgcolor:'rgba(0,0,0,0.45)' }, boxShadow:1 }}
                                                                onClick={(e) => { e.stopPropagation(); openInfoDebounced(e.currentTarget, { ...a, tooltipTitle }); }}
                                                            >
                                                                <InfoOutlined sx={{ fontSize:14 }} />
                                                            </IconButton>
                                                            {(() => {
                                                                const startLabel = timeOnly(a.startUtc, preferences);
                                                                const endLabel = timeOnly(a.endUtc, preferences);
                                                                const name = a.patientName || '(no name)';
                                                                const compact = layout.rect.height < rowHeight;
                                                                if (compact) {
                                                                    return (
                                                                        <Box sx={{ position:'absolute', inset:0, display:'flex', alignItems:'center', pl:0.4, pr:2.4, overflow:'hidden' }}>
                                                                            <Typography variant="caption" sx={{ fontSize: 9, fontWeight:600, whiteSpace:'nowrap', textOverflow:'ellipsis', overflow:'hidden', lineHeight:1.1 }}>
                                                                                {name} · {startLabel}-{endLabel}{a.encounterName ? ` • ${a.encounterName}` : ''}
                                                                            </Typography>
                                                                        </Box>
                                                                    );
                                                                }
                                                                return (
                                                                    <>
                                                                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11, lineHeight:1.1, pr:2 }}>{name}</Typography>
                                                                        <Typography variant="caption" display="block" sx={{ fontSize: 10, lineHeight:1.1, pr:2 }}>{startLabel} - {endLabel}{a.encounterName ? ` • ${a.encounterName}` : ''}</Typography>
                                                                    </>
                                                                );
                                                            })()}
                                                        </Box>
                                                    ) : (
                                                    <StyledTooltip key={`${a.id}-${a.startUtc}`} title={<Box component="span" sx={{ whiteSpace:'pre-line' }}>{tooltipTitle}</Box>} arrow enterDelay={350} placement="right" TransitionProps={{ timeout:180 }}>
                                                    <Box 
                                                        key={`box-${a.id}-${a.startUtc}`} 
                                                        onMouseDown={onMouseDown} 
                                                        onClick={onClick}
                                                        onContextMenu={onContextMenu}
                                                        sx={{ 
                                                            position: "absolute", 
                                                            left: `${leftOffset}%`, 
                                                            width: `${columnWidth}%`, 
                                                            top: layout.rect.top, 
                                                            height: layout.rect.height, 
                                                            zIndex: layout.maxColumns > 1 ? 3 : 2, 
                                                            bgcolor: statusColors.bgcolor, 
                                                            border: statusColors.border, 
                                                            borderRadius: 0.5, 
                                                            p: 0.3, 
                                                            cursor: "grab", 
                                                            userSelect: "none", 
                                                            fontSize: 11, 
                                                            transition: "box-shadow 120ms, border-color 120ms, background-color 120ms", 
                                                            '&:hover': { 
                                                                boxShadow: 2, 
                                                                borderColor: statusColors.hoverBorder, 
                                                                bgcolor: statusColors.hoverBg 
                                                            } 
                                                        }}
                                                    >
                                                        {/* Resize handles */}
                                                        <Box
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                                const containerRect = containerRef.current?.getBoundingClientRect();
                                                                if (!containerRect) return;
                                                                const d = {
                                                                    id: a.id,
                                                                    resize: 'top',
                                                                    activated: false,
                                                                    originMouseY: e.clientY,
                                                                    originStart: new Date(a.startUtc),
                                                                    originEnd: new Date(a.endUtc),
                                                                    originPid: p.id,
                                                                    originLocationId: locationId,
                                                                    originDayIdx: dayIdx,
                                                                    baseDate,
                                                                    days,
                                                                    practitionerIds: practitionersShown.map(pp => pp.id),
                                                                    containerLeft: containerRect.left,
                                                                    containerTop: containerRect.top,
                                                                    containerWidth: containerRect.width,
                                                                    headerHeight,
                                                                    openStart: openClose.start,
                                                                    weeklyByPid: Object.fromEntries(practitionersShown.map(pp => [pp.id, getWeeklyFor(pp.id, locationId)])),
                                                                    timeOffSnapshot: [...timeOff],
                                                                    apptsSnapshot: [...appointments]
                                                                };
                                                                dragRef.current = d;
                                                            }}
                                                            data-resize-handle="top"
                                                            sx={() => {
                                                                const active = dragRef.current && dragRef.current.id === a.id && dragRef.current.resize === 'top';
                                                                return {
                                                                    position: 'absolute', top: 0, left: 0, right: 0, height: 10,
                                                                    cursor: 'ns-resize', zIndex: 5,
                                                                    borderTopLeftRadius: 2, borderTopRightRadius: 2,
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    background: active ? 'linear-gradient(90deg, #1976d2, #42a5f5)' : 'rgba(0,0,0,0.04)',
                                                                    opacity: active ? 0.95 : 0.55,
                                                                    transition: 'background-color 120ms, opacity 120ms',
                                                                    '&:after': {
                                                                        content: '""',
                                                                        width: 24,
                                                                        height: 3,
                                                                        borderRadius: 2,
                                                                        background: active ? 'rgba(255,255,255,0.9)' : 'linear-gradient(90deg, rgba(0,0,0,0.25) 25%, rgba(0,0,0,0.10) 25%, rgba(0,0,0,0.10) 50%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.25) 75%, rgba(0,0,0,0.10) 75%)',
                                                                        backgroundSize: '12px 3px'
                                                                    }
                                                                };
                                                            }}
                                                        />
                                                        <Box
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                                const containerRect = containerRef.current?.getBoundingClientRect();
                                                                if (!containerRect) return;
                                                                const d = {
                                                                    id: a.id,
                                                                    resize: 'bottom',
                                                                    activated: false,
                                                                    originMouseY: e.clientY,
                                                                    originStart: new Date(a.startUtc),
                                                                    originEnd: new Date(a.endUtc),
                                                                    originPid: p.id,
                                                                    originLocationId: locationId,
                                                                    originDayIdx: dayIdx,
                                                                    baseDate,
                                                                    days,
                                                                    practitionerIds: practitionersShown.map(pp => pp.id),
                                                                    containerLeft: containerRect.left,
                                                                    containerTop: containerRect.top,
                                                                    containerWidth: containerRect.width,
                                                                    headerHeight,
                                                                    openStart: openClose.start,
                                                                    weeklyByPid: Object.fromEntries(practitionersShown.map(pp => [pp.id, getWeeklyFor(pp.id, locationId)])),
                                                                    timeOffSnapshot: [...timeOff],
                                                                    apptsSnapshot: [...appointments]
                                                                };
                                                                dragRef.current = d;
                                                            }}
                                                            data-resize-handle="bottom"
                                                            sx={() => {
                                                                const active = dragRef.current && dragRef.current.id === a.id && dragRef.current.resize === 'bottom';
                                                                return {
                                                                    position: 'absolute', bottom: 0, left: 0, right: 0, height: 10,
                                                                    cursor: 'ns-resize', zIndex: 5,
                                                                    borderBottomLeftRadius: 2, borderBottomRightRadius: 2,
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    background: active ? 'linear-gradient(90deg, #1976d2, #42a5f5)' : 'rgba(0,0,0,0.04)',
                                                                    opacity: active ? 0.95 : 0.55,
                                                                    transition: 'background-color 120ms, opacity 120ms',
                                                                    '&:after': {
                                                                        content: '""',
                                                                        width: 24,
                                                                        height: 3,
                                                                        borderRadius: 2,
                                                                        background: active ? 'rgba(255,255,255,0.9)' : 'linear-gradient(90deg, rgba(0,0,0,0.25) 25%, rgba(0,0,0,0.10) 25%, rgba(0,0,0,0.10) 50%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.25) 75%, rgba(0,0,0,0.10) 75%)',
                                                                        backgroundSize: '12px 3px'
                                                                    }
                                                                };
                                                            }}
                                                        />
                                                        {(() => {
                                                            const startLabel = timeOnly(a.startUtc, preferences);
                                                            const endLabel = timeOnly(a.endUtc, preferences);
                                                            const name = toTitleCase(a.patientName) || '(no name)';
                                                            //const phone = 
                                                            const compact = layout.rect.height < rowHeight; // <=30m block
                                                            if (compact) {
                                                                return (
                                                                    <Box sx={{ position:'absolute', inset:0, display:'flex', alignItems:'center', pl:0.4, pr:0.4, overflow:'hidden' }}>
                                                                        <Typography variant="caption" sx={{ fontSize: 9, fontWeight:600, whiteSpace:'nowrap', textOverflow:'ellipsis', overflow:'hidden', lineHeight:1.1 }}>
                                                                            {name} · {startLabel}-{endLabel}{a.encounterName ? ` • ${a.encounterName}` : ''}
                                                                        </Typography>
                                                                    </Box>
                                                                );
                                                            }
                                                            return (
                                                                <>
                                                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11, lineHeight:1.1 }}>{name}</Typography>
                                                                    <Typography variant="caption" display="block" sx={{ fontSize: 10, lineHeight:1.1 }}>{startLabel} - {endLabel}{a.encounterName ? ` • ${a.encounterName}` : ''}</Typography>
                                                                </>
                                                            );
                                                        })()}
                                                    </Box>
                                                    </StyledTooltip>
                                                    )
                                                );
                                            });
                                        })()}
                                    </Box>
                                );
                            })}
                        </Stack>
                    ))}
                </Stack>
                {/* Hover time line (snaps to 15-minute increments) */}
                {hoverY != null && (() => {
                    const stepPx = rowHeight / 2; // 15m
                    const mins = clamp(Math.round(((hoverY - headerHeight) / stepPx)) * 15, 0, 24 * 60);
                    const base = fromLocalDateTime(baseDate, openClose.start, preferences.timezone);
                    const t = new Date(base.getTime() + mins * 60000);
                    const label = formatTimeLocal(t, preferences.timezone);
                    const snappedY = headerHeight + (mins / 30) * rowHeight;
                    return (
                        <>
                            <Box sx={{ position: "absolute", left: 64, right: 0, top: snappedY, height: 0, borderTop: "1px solid #ff7043", pointerEvents: "none" }} />
                            <Box sx={{ position: "absolute", top: snappedY - 10, left: 66, bgcolor: "background.paper", border: "1px solid #ff7043", color: "text.primary", borderRadius: 1, px: 0.5, py: 0, fontSize: 12, zIndex: 2, pointerEvents: "none" }}>{label}</Box>
                        </>
                    );
                })()}
            </Box>
        );
    };

    // Drag move listener on window to support moving appointments
    // window-level drag handlers (mount once)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const onMove = (e) => {
                        
            const d = dragRef.current; if (!d) return;
            const isResize = !!d.resize;
            const isCreate = !!d.create;
            const threshold = 6; // px vertical dead-zone
            if (!d.activated) {
                const dy = Math.abs(e.clientY - d.originMouseY);
                if (dy < threshold) {
                    return; // ignore tiny movements
                }
                d.activated = true; // activate drag after surpassing threshold
                // For move operations (not resize/create) we need originStart/end set earlier; they already are.
                if (isCreate) {
                    // initialize originStart/End mirrors once active
                    d.originStart = d.originCreateStart;
                    d.originEnd = new Date(d.originCreateStart.getTime() + 15 * 60000);
                }
            }
            // Determine target day/practitioner from X unless resizing (resize stays same day/practitioner)
            const gridWidth = Math.max(0, d.containerWidth - 64);
            const dayWidth = gridWidth / d.days;
            const colWidth = dayWidth / d.practitionerIds.length;
            let targetDayIdx = d.originDayIdx;
            let targetPid = d.originPid;
            if (!isResize && !isCreate) {
                let localX = e.clientX - d.containerLeft - 64;
                if (!isFinite(localX)) localX = 0;
                localX = Math.max(0, Math.min(gridWidth - 1, localX));
                targetDayIdx = Math.max(0, Math.min(d.days - 1, Math.floor(localX / dayWidth)));
                const withinDayX = localX - targetDayIdx * dayWidth;
                const targetColIdx = Math.max(0, Math.min(d.practitionerIds.length - 1, Math.floor(withinDayX / colWidth)));
                targetPid = d.practitionerIds[targetColIdx];
            }

            // Determine target time from Y with 15-min snapping
            let localY = e.clientY - d.containerTop;
            const stepPx = rowHeight / 2; // 15 minutes
            const mins = clamp(Math.round(((localY - d.headerHeight) / stepPx)) * 15, 0, 24 * 60);
            const targetDate = addDays(new Date(d.baseDate), targetDayIdx);
            const base = fromLocalDateTime(targetDate, d.openStart);
            const snapped = new Date(base.getTime() + mins * 60000);

            let newStart = d.originStart;
            let newEnd = d.originEnd;
            if (isCreate) {
                // For creation, originCreateStart is fixed start; drag defines end (downward only)
                const start = d.originCreateStart;
                // Minimum 15 minutes
                const snappedEnd = snapped > start ? snapped : new Date(start.getTime() + 15 * 60000);
                newStart = start;
                newEnd = new Date(Math.max(snappedEnd.getTime(), start.getTime() + 15 * 60000));
            }
            if (isResize) {
                if (d.resize === 'top') {
                    // Prevent inversion; keep at least 15 minutes
                    if (snapped < newEnd && (newEnd - snapped) >= 15 * 60000) {
                        newStart = snapped;
                    }
                } else if (d.resize === 'bottom') {
                    if (snapped > newStart && (snapped - newStart) >= 15 * 60000) {
                        newEnd = snapped;
                    }
                }
            } else if (!isCreate) {
                const durationMs = d.originEnd.getTime() - d.originStart.getTime();
                newStart = snapped;
                newEnd = new Date(snapped.getTime() + durationMs);
            }

            // Build pending with local datetime strings (not UTC)
            d.pending = { 
                startUtc: toLocalDateTimeInputValue(newStart, tenantTimezone), 
                endUtc: toLocalDateTimeInputValue(newEnd, tenantTimezone), 
                practitionerId: targetPid, 
                locationId: d.originLocationId 
            };

            // Determine schedule for target using overrides first
            let ds = null;
            try {
                const dayStr = toLocalDateInputValue(new Date(targetDate), tenantTimezone);
                const oKey = `${targetPid}-${d.originLocationId}-${dayStr}`;
                if (dayOverrides.has(oKey)) ds = dayOverrides.get(oKey);
                else ds = (d.weeklyByPid[targetPid] || {})[new Date(targetDate).getDay()];
            } catch {
                ds = (d.weeklyByPid[targetPid] || {})[new Date(targetDate).getDay()];
            }
            const inside = ds && ds.enabled && ds.start && ds.end && (newStart >= fromLocalDateTime(targetDate, ds.start)) && (newEnd <= fromLocalDateTime(targetDate, ds.end));
            const offBlock = d.timeOffSnapshot.find(off => off.practitionerId === targetPid && !(newEnd <= new Date(off.startUtc) || newStart >= new Date(off.endUtc)));
            const conflictWith = d.apptsSnapshot.find(a => a.practitionerId === targetPid && a.id !== d.id && !(newEnd <= new Date(a.startUtc) || newStart >= new Date(a.endUtc)));
            d.pendingValid = !!inside && !offBlock && (allowOverlapping || !conflictWith);
            d.pendingMeta = { inside: !!inside, offBlock: offBlock || null, conflictWith: conflictWith || null, targetDate: targetDate, resize: d.resize, create: isCreate };
        };
        const onUp = () => {

            const d = dragRef.current; 
            if (!d) return;
            const pending = d.pending;
            const wasActivated = d.activated;
            dragRef.current = null; // Clear immediately
            // If drag never activated (dead-zone), treat as simple click (creation) or ignore (resize/move)
            if (!wasActivated) {
                if (!canCreateAppointment) {
                    showMsg("You do not have permission to create appointments", "error");
                    return;
                }                  
                if (d.create) {
                    // mimic click-create fallback using originCreateStart
                    const start = d.originCreateStart;
                    const end = new Date(start.getTime() + 15 * 60000);

                    const payload = { 
                        practitionerId: d.originPid, 
                        locationId: d.originLocationId, 
                        startUtc: toLocalDateTimeInputValue(start, tenantTimezone), 
                        endUtc: toLocalDateTimeInputValue(end, tenantTimezone), 
                        patientName: '', 
                        notes: '' 
                    };
                    setBookingDialog({ open: true, event: payload, mode: 'create' });
                }
                return;
            }
            if (!pending) return;
            if (!d.pendingValid) return; // silently cancel invalid
            if (d.create) {
                const payload = { ...pending, patientName: '', notes: '' };
                setBookingDialog({ open: true, event: payload, mode: 'create' });
            } else {
                const apptId = d.id;
                const original = appointments.find(a => a.id === apptId);
                if (!original) return;
                setAppointmentChangeConfirmation({
                    open: true,
                    change: {
                        original,
                        pending: { ...pending },
                        operation: d.resize ? `resize-${d.resize === 'top' ? 'start' : 'end'}` : 'move',
                        originalPractitionerName: practitionersMap.get(original.practitionerId)?.fullName || `#${original.practitionerId}`,
                        proposedPractitionerName: practitionersMap.get(pending.practitionerId)?.fullName || `#${pending.practitionerId}`
                    }
                });
            }
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    }, [allowOverlapping, dayOverrides, appointments, practitionersMap, canCreateAppointment, tenantTimezone]);

    // -------------------------
    // Month View (simple)
    // -------------------------
    const MonthView = () => {
        const first = new Date(activeDate.getFullYear(), activeDate.getMonth(), 1);
        const start = new Date(first);
        // Start on Sunday
        start.setDate(first.getDate() - first.getDay());
        const days = Array.from({ length: 42 }, (_, i) => addDays(start, i));
        const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

        // Keyboard navigation for month grid
        const [focusedDate, setFocusedDate] = useState(() => new Date(activeDate));
    // Keep month focus in sync with activeDate
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setFocusedDate(new Date(activeDate)); }, [activeDate]);
        useEffect(() => {
            const iso = toLocalDateInputValue(focusedDate, tenantTimezone);
            const el = document.querySelector(`[data-month-cell="${iso}"]`);
            if (el) { el.focus(); }
        }, [focusedDate]);
        const onMonthKeyDown = (e) => {
            const key = e.key;
            let nextDate = null;
            if (key === 'ArrowLeft') { nextDate = addDays(focusedDate, -1); }
            else if (key === 'ArrowRight') { nextDate = addDays(focusedDate, 1); }
            else if (key === 'ArrowUp') { nextDate = addDays(focusedDate, -7); }
            else if (key === 'ArrowDown') { nextDate = addDays(focusedDate, 7); }
            else if (key === 'PageUp') { nextDate = new Date(focusedDate.getFullYear(), focusedDate.getMonth() - 1, focusedDate.getDate()); }
            else if (key === 'PageDown') { nextDate = new Date(focusedDate.getFullYear(), focusedDate.getMonth() + 1, focusedDate.getDate()); }
            else if (key === 'Enter' || key === ' ') { setView('day'); setActiveDate(focusedDate); e.preventDefault(); return; }
            if (nextDate) { setFocusedDate(nextDate); e.preventDefault(); }
        };

        return (
            <Box>
                {/* Weekday header */}
                <Stack direction="row" sx={{ mb: 0.5 }}>
                    {weekdayLabels.map((label, idx) => (
                        <Box key={idx} sx={{ flex: 1, px: 1, py: 0.5, textAlign: "center", color: "text.secondary", fontSize: 12, fontWeight: 600 }}>
                            {label}
                        </Box>
                    ))}
                </Stack>
                {/* Practitioner color legend (click to toggle) */}
                <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                    {practitionersShown.map(p => {
                        const pc = practitionerColorMap.get(p.id) || { dot: theme.palette.primary.main, border: alpha(theme.palette.primary.main, 0.35), bg: alpha(theme.palette.primary.main, 0.10) };
                        const selected = selectedPractitionerIds.includes(p.id);
                        return (
                            <Box
                                key={p.id}
                                role="button"
                                aria-pressed={selected}
                                onClick={() => {
                                    setSelectedPractitionerIds(prev => selected ? prev.filter(id => id !== p.id) : [...prev, p.id]);
                                }}
                                sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 0.75,
                                    border: `1px solid ${pc.border}`, bgcolor: pc.bg, borderRadius: 10, px: 1, py: 0.3,
                                    cursor: 'pointer', opacity: selected ? 1 : 0.5,
                                    '&:hover': { boxShadow: 1 }
                                }}
                                title={selected ? 'Hide practitioner' : 'Show practitioner'}
                            >
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: pc.dot }} />
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                            </Box>
                        );
                    })}
                </Stack>
                {/* Calendar grid */}
                <Box role="grid" tabIndex={0} onKeyDown={onMonthKeyDown} sx={{ outline: 'none', display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
                    {days.map((d, i) => {
                        const isCurMonth = d.getMonth() === activeDate.getMonth();
                        const isToday = sameDay(d, new Date());
                        const dayAppointments = appointments
                            .filter(a =>
                                new Date(a.startUtc).getMonth() === d.getMonth() &&
                                sameDay(a.startUtc, d) &&
                                selectedPractitionerIds.includes(a.practitionerId) &&
                                a.locationId === locationId &&
                                (!encounterFilterId || a.encounterId === encounterFilterId)
                            )
                            .sort((a, b) => new Date(a.startUtc) - new Date(b.startUtc));
                        const preview = dayAppointments.slice(0, 3);
                        const overflow = Math.max(0, dayAppointments.length - preview.length);

                        return (
                            <Box
                                key={i}
                                onClick={() => { setView("day"); setActiveDate(d); }}
                                sx={{
                                    border: "1px solid",
                                    borderColor: isCurMonth ? "#e6e8eb" : "#f0f0f0",
                                    borderRadius: 1,
                                    p: 1,
                                    minHeight: 110,
                                    bgcolor: isCurMonth ? "background.paper" : "#fafafa",
                                    cursor: "pointer",
                                    position: "relative",
                                    overflow: "hidden",
                                    transition: "box-shadow 160ms, transform 80ms",
                                    '&:hover': { boxShadow: 2 },
                                }}
                                role="gridcell"
                                aria-selected={toLocalDateInputValue(d, tenantTimezone) === toLocalDateInputValue(focusedDate, tenantTimezone)}
                                tabIndex={toLocalDateInputValue(d, tenantTimezone) === toLocalDateInputValue(focusedDate, tenantTimezone) ? 0 : -1}
                                data-month-cell={toLocalDateInputValue(d, tenantTimezone)}
                            >
                                {/* Date header */}
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="caption" sx={{ color: isCurMonth ? 'text.secondary' : '#bdbdbd', fontWeight: 600 }}>
                                        {d.getDate()}
                                    </Typography>
                                    {isToday && (
                                        <Box sx={{ bgcolor: 'primary.main', color: '#fff', borderRadius: 10, px: 0.8, py: 0.1, fontSize: 10, fontWeight: 700 }}>
                                            Today
                                        </Box>
                                    )}
                                </Stack>
                                {/* Appointments preview */}
                                <Stack spacing={0.5} sx={{ mt: 1 }}>
                                    {preview.map((a) => {
                                        const pc = practitionerColorMap.get(a.practitionerId) || { bg: 'rgba(25,118,210,0.08)', border: 'rgba(25,118,210,0.25)', dot: '#1976d2' };
                                        const prName = practitionersMap.get(a.practitionerId)?.fullName || `#${a.practitionerId}`;
                                        const namePart = a.patientName || '(no name)';
                                        const agePart = a.patientAge != null ? ` (${a.patientAge})` : '';
                                        const label = `${formatTimeLocal(a.startUtc, preferences.timezone)} • ${prName} • ${namePart}${agePart}${a.notes ? ' • ' + a.notes : ''}`;
                                        return (
                                            <Tooltip key={a.id} title={label} arrow>
                                                <Box sx={{
                                                    bgcolor: pc.bg,
                                                    border: `1px solid ${pc.border}`,
                                                    borderRadius: 0.5,
                                                    px: 0.6,
                                                    py: 0.2,
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.6,
                                                    cursor: 'pointer'
                                                }}
                                                    onClick={(e) => { e.stopPropagation(); setBookingDialog({ open: true, event: a, mode: 'edit' }); }}
                                                >
                                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: pc.dot, flex: '0 0 auto' }} />
                                                    <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                        {formatTimeLocal(a.startUtc, preferences.timezone)} · {a.patientName || '(no name)'}{a.patientAge != null && ` (${a.patientAge})`}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        );
                                    })}
                                    {overflow > 0 && (
                                        <Typography 
                                            variant="caption" 
                                            sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                            onClick={(e) => { e.stopPropagation(); setMonthOverflow({ anchorEl: e.currentTarget, date: d }); }}
                                        >
                                            +{overflow} more
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    // -------------------------
    // Render
    // -------------------------
    return (
        <Box>
            <AppointmentToolbar 
                activeDate={activeDate}
                setActiveDate={setActiveDate}
                view={view}
                setView={setView}
                locationId={locationId}
                setLocationId={setLocationId}
                setLocation={setLocation}
                selectedPractitionerIds={selectedPractitionerIds}
                setSelectedPractitionerIds={setSelectedPractitionerIds}
                allowOverlapping={allowOverlapping}
                setAllowOverlapping={setAllowOverlapping}
                setActionsMenu={setActionsMenu}
                encounterFilterId={encounterFilterId}
                setEncounterFilterId={setEncounterFilterId}
                encounters={encounters}
                onPrev={prev}
                onNext={next}
                onGotoToday={gotoToday}
            />
            <Divider sx={{ mb: 2 }} />
            
            {/* Customer booking banner */}
            {customerContext && customerContext.customerId && (
                <Alert 
                    severity="info" 
                    sx={{ mb: 2 }}
                    onClose={() => {
                        // Clear customer context by replacing history state
                        window.history.replaceState({}, document.title);
                        window.location.reload(); // Simple reload to clear context
                    }}
                >
                    <Typography variant="body2">
                        <strong>Booking for:</strong> {customerContext.customerName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Select location and practitioner above, then click on the schedule to book an appointment.
                    </Typography>
                </Alert>
            )}
            
            {/* Practitioner legend for day/week views */}
            {(view === 'day' || view === 'week') && selectedPractitionerIds.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                    {/* Legend population will use real practitioners once loaded; placeholder hides when none selected */}
                </Stack>
            )}
            <Box id="scheduler-grid-root">
                {view === "day" && <TimeGrid baseDate={activeDate} days={1} />}
                {view === "week" && <TimeGrid baseDate={addDays(activeDate, -activeDate.getDay())} days={7} />}
                {view === "month" && <MonthView />}
            </Box>

            {/* Appointment actions menu */}
            <Menu
                anchorReference="anchorPosition"
                anchorPosition={contextMenu.anchorPosition}
                open={Boolean(contextMenu.anchorPosition)}
                onClose={handleMenuClose}
                transformOrigin={{ 
                    vertical: 'top', 
                    horizontal: 'left' 
                }}
                MenuListProps={{
                    sx: { 
                        minWidth: 180,
                        py: 1
                    }
                }}
            >
                {/* Quick actions (desktop) */}
                {contextMenu.appt && (contextMenu.appt.patientId || contextMenu.appt.patientEmail || contextMenu.appt.patientMobile) && (
                    <>
                        {contextMenu.appt.patientId && (
                            <MenuItem onClick={() => { handleMenuClose(); navigate(`/customerrecord/${contextMenu.appt.patientId}`); }}>
                                Open Patient Record
                            </MenuItem>
                        )}
                        {contextMenu.appt.patientEmail && (
                            <MenuItem onClick={() => { handleMenuClose(); window.location.href = `mailto:${contextMenu.appt.patientEmail}`; }}>
                                Email Patient
                            </MenuItem>
                        )}
                        {canSendText && contextMenu.appt.patientMobile && (
                            <MenuItem onClick={() => { handleMenuClose(); setSendSmsDialog({ open: true, appointment: contextMenu.appt }); }}>
                                Text Patient
                            </MenuItem>
                        )}
                        <Divider />
                    </>
                )}
                <MenuItem onClick={() => { handleMenuClose(); setBookingDialog({ open: true, event: contextMenu.appt, mode: "edit" }); }}>Edit</MenuItem>
                <Divider />
                <Typography variant="caption" sx={{ px: 2, pt: 1, pb: 0.5, display: 'block', color: 'text.secondary' }}>Reassign to</Typography>
                {practitionersShown.map(p => (
                    <MenuItem key={p.id} onClick={() => handleReassign(p.id)} disabled={p.id === contextMenu.appt?.practitionerId}>{p.name}</MenuItem>
                ))}
                <Divider />
                <MenuItem onClick={() => { handleMenuClose(); setMoveToDate({ open: true, appt: contextMenu.appt }); }}>Move to date…</MenuItem>
            </Menu>

            {/* Actions menu */}
            <Can section="scheduling" action="create-schedule">
                <Menu
                    anchorEl={actionsMenu.anchorEl}
                    open={Boolean(actionsMenu.anchorEl)}
                    onClose={() => setActionsMenu({ anchorEl: null })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >   
                    {
                        view === 'day' && (<MenuItem onClick={() => {
                            setActionsMenu({ anchorEl: null });
                            const ids = selectedPractitionerIds || [];
                            if (ids.length === 0) { showMsg('Select a practitioner first', 'warning'); return; }
                            if (ids.length === 1) {
                                const pid = ids[0];
                                const base = getDaySchedule(activeDate, pid, locationId);
                                if (!base || !base.enabled) { showMsg('No recurring schedule on this date', 'warning'); return; }
                                setDayOverrideDialog({ open: true, practitionerId: pid, locationId, date: activeDate });
                            } else {
                                setPractitionerPicker({ open: true, intent: 'day-override', date: activeDate });
                            }
                        }}>
                            Edit Schedule (this date)
                        </MenuItem>)
                    }

                    <MenuItem onClick={() => {
                        setActionsMenu({ anchorEl: null });
                        const ids = selectedPractitionerIds || [];
                        if (ids.length === 0) { showMsg('Select a practitioner first', 'warning'); return; }
                        if (ids.length === 1) {
                            const pid = ids[0];
                            setScheduleDialog({ open: true, practitionerId: pid, locationId, mode: 'create' });
                        } else {
                            // Multiple selected → prompt to choose one for full series scheduling
                            setPractitionerPicker({ open: true, intent: 'series', date: activeDate });
                        }
                    }}>
                        <Schedule sx={{ mr: 1 }} />
                        Add Schedule
                    </MenuItem>
                    <MenuItem onClick={() => { setActionsMenu({ anchorEl: null }); setTimeOffDialog({ open: true, practitionerId: selectedPractitionerIds[0], mode: 'create', timeOff: null }); }}>
                        <EventAvailable sx={{ mr: 1 }} />
                        Add Time Off
                    </MenuItem>
                    <Divider />
                </Menu>
            </Can>            

            {/* dialogs */}
            <BookingDialog
                open={bookingDialog.open}
                event={bookingDialog.event}
                mode={bookingDialog.mode}
                onClose={() => setBookingDialog({ open: false, event: null, mode: "create" })}
                onDelete={(id) => { deleteAppointment(id); setBookingDialog({ open: false, event: null, mode: "create" }); }}
                onSave={(payload) => { const ok = upsertAppointment(payload); if (ok) setBookingDialog({ open: false, event: null, mode: "create" }); }}
                defaultLocationId={locationId}
                defaultPractitionerId={selectedPractitionerIds[0]}
            />
            <ScheduleDialogCmp
                open={scheduleDialog.open}
                practitionerId={scheduleDialog.practitionerId}
                locationId={scheduleDialog.locationId}
                mode={scheduleDialog.mode}
                schedules={schedules}
                makeDefaultWeekly={() => ({ _meta: { frequency: 'weekly', start: toLocalDateInputValue(new Date(), tenantTimezone), end: { type: 'on', date: toLocalDateInputValue(addDays(new Date(), 7), tenantTimezone) }, nonRepeatEntries: [] } })}
                onSaveDraft={({ pid, lid, draft }) => {
                    const normalizedDraft = {
                        ...(draft || {}),
                        _meta: {
                            ...(draft?._meta || {}),
                            start: draft?._meta?.start || toLocalDateInputValue(new Date(), tenantTimezone),
                            end: draft?._meta?.end || { type: 'on', date: toLocalDateInputValue(addDays(new Date(), 7), tenantTimezone) },
                        },
                    };

                    // Persist to backend, then update local cache
                    ScheduleService.upsertWeeklyV2({ practitionerId: pid, locationId: lid, draft: normalizedDraft })
                        .then(() => {
                            showMsg("Schedule saved", "success");
                            // Re-fetch schedules from backend truth while preserving current UI selection state.
                            setScheduleRefreshTick(prev => prev + 1);
                        })
                        .catch(() => {
                            // toast handled globally; keep dialog open for retry
                        })
                        .finally(() => {
                            setScheduleDialog({ open: false, practitionerId: null, locationId: null, mode: 'create' });
                        });
                }}
                onClose={() => setScheduleDialog({ open: false, practitionerId: null, locationId: null, mode: 'create' })}
            />
            <TimeOffDialogCmp
                open={timeOffDialog.open}
                practitionerId={timeOffDialog.practitionerId}
                activeDate={activeDate}
                mode={timeOffDialog.mode}
                timeOff={timeOffDialog.timeOff}
                schedules={schedules}
                locationId={locationId}
                showMsg={showMsg}
                onSave={async (payload) => {
                    try {
                        // Create or update
                        if (timeOffDialog.mode === 'edit' && payload?.id && Array.isArray(payload.items)) {
                            // Simplest approach: delete the existing record and recreate the new set for that day range
                            await ScheduleService.deleteTimeOff(payload.id);
                            await Promise.all(payload.items.map(it => ScheduleService.createTimeOff({ ...it, locationId })));
                            showMsg('Time off updated', 'success');
                        } else {
                            const items = Array.isArray(payload) ? payload : [payload];
                            await Promise.all(items.map(it => ScheduleService.createTimeOff({ ...it, locationId })));
                            showMsg(items.length > 1 ? `${items.length} time-off entries saved` : 'Time off saved', 'success');
                        }
                        // reload time off for current range
                        const weekStart = addDays(activeDate, -((activeDate.getDay() + 6) % 7));
                        const weekEnd = addDays(weekStart, 6);
                        const startStr = toLocalDateInputValue(weekStart, tenantTimezone);
                        const endStr = toLocalDateInputValue(weekEnd, tenantTimezone);
                        const res = await ScheduleService.listTimeOff({ practitioners: selectedPractitionerIds, start: startStr, end: endStr, locationId });
                        if (res?.code === 0 && Array.isArray(res.data)) {
                            const normalizeLocalDateTime = (s) => parseLocalDateTimeString(s);
                            const mapped = res.data.map(r => ({
                                id: r.id,
                                practitionerId: r.EmployeeId || r.practitionerId,
                                startUtc: normalizeLocalDateTime(r.startDate),
                                endUtc: normalizeLocalDateTime(r.endDate),
                                reason: r.reason || ''
                            }));
                            setTimeOff(mapped);
                        }
                    } catch {
                        // toast handled globally
                    }
                }}
                onDelete={() => setTimeOffDeleteConfirmation({ open: true, timeOff: timeOffDialog.timeOff })}
                onClose={() => setTimeOffDialog({ open: false, practitionerId: null })}
            />
            <Dialog
                open={timeOffDeleteConfirmation.open}
                onClose={() => {
                    if (!deletingTimeOff) setTimeOffDeleteConfirmation({ open: false, timeOff: null });
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete time off?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This time-off slot will be permanently removed.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={deletingTimeOff}
                        onClick={() => setTimeOffDeleteConfirmation({ open: false, timeOff: null })}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deletingTimeOff}
                        onClick={deleteTimeOffEntry}
                    >
                        {deletingTimeOff ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
            <MoveToDateDialogCmp
                open={moveToDate.open}
                appt={moveToDate.appt}
                activeDate={activeDate}
                onSave={(payload) => { const ok = upsertRef.current(payload); if (ok) setMoveToDate({ open: false, appt: null }); }}
                onClose={() => setMoveToDate({ open: false, appt: null })}
            />

            <AppointmentChangeConfirmationDialog
                open={appointmentChangeConfirmation.open}
                change={appointmentChangeConfirmation.change}
                preferences={{ ...preferences, timezone: tenantTimezone }}
                saving={appointmentChangeSaving}
                onCancel={closeAppointmentChangeConfirmation}
                onConfirm={confirmAppointmentChange}
            />

            {/* Outlook-style prompt: this date only vs entire series */}
            <Dialog
                open={confirmEditScope.open}
                onClose={() => setConfirmEditScope({ open: false, practitionerId: null, locationId: null, date: null })}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Edit schedule</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Edit only this date ({toLocalDateInputValue(confirmEditScope.date || new Date(), tenantTimezone)}) or the entire series?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
                        <Button
                            onClick={() => {
                                const { practitionerId, locationId: lid, date } = confirmEditScope;
                                setConfirmEditScope({ open: false, practitionerId: null, locationId: null, date: null });
                                setDayOverrideDialog({ open: true, practitionerId, locationId: lid, date: date || activeDate });
                            }}
                        >
                            This date only
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                const { practitionerId, locationId: lid } = confirmEditScope;
                                setConfirmEditScope({ open: false, practitionerId: null, locationId: null, date: null });
                                setScheduleDialog({ open: true, practitionerId, locationId: lid, mode: 'edit' });
                            }}
                        >
                            Entire series
                        </Button>
                        <Button onClick={() => setConfirmEditScope({ open: false, practitionerId: null, locationId: null, date: null })}>Close</Button>
                    </Box>
                    <Divider sx={{ width: '100%' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                        Delete schedule
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
                        <Button
                            color="error"
                            onClick={() => {
                                const { practitionerId, locationId: lid, date } = confirmEditScope;
                                const dateString = toLocalDateInputValue(date || activeDate, tenantTimezone);
                                ScheduleService.setDayOverrideV2({
                                    practitionerId,
                                    locationId: lid,
                                    date: dateString,
                                    override: { enabled: false },
                                })
                                    .then(() => {
                                        const key = `${practitionerId}-${lid}-${dateString}`;
                                        setDayOverrides(prev => {
                                            const next = new Map(prev);
                                            next.set(key, { enabled: false });
                                            return next;
                                        });
                                        setScheduleRefreshTick(prev => prev + 1);
                                        showMsg('Schedule deleted for this day', 'warning');
                                    })
                                    .finally(() => {
                                        setConfirmEditScope({ open: false, practitionerId: null, locationId: null, date: null });
                                    });
                            }}
                        >
                            Delete this day
                        </Button>
                        <Button
                            color="error"
                            variant="contained"
                            onClick={() => {
                                const { practitionerId, locationId: lid, date } = confirmEditScope;
                                const dateString = toLocalDateInputValue(date || activeDate, tenantTimezone);
                                ScheduleService.upsertWeeklyV2({
                                    practitionerId,
                                    locationId: lid,
                                    date: dateString,
                                    draft: {},
                                    delete: true,
                                })
                                    .then(() => {
                                        const overridePrefix = `${practitionerId}-${lid}-`;
                                        setDayOverrides(prev => {
                                            const next = new Map(prev);
                                            for (const key of next.keys()) {
                                                if (key.startsWith(overridePrefix)) next.delete(key);
                                            }
                                            return next;
                                        });
                                        setScheduleRefreshTick(prev => prev + 1);
                                        showMsg('Entire schedule series deleted', 'warning');
                                    })
                                    .finally(() => {
                                        setConfirmEditScope({ open: false, practitionerId: null, locationId: null, date: null });
                                    });
                            }}
                        >
                            Delete entire series
                        </Button>
                    </Box>
                    {(() => {
                        const { practitionerId, date } = confirmEditScope;
                        const selectedDate = date || activeDate;
                        const dayStart = fromLocalDateTime(selectedDate, '00:00', tenantTimezone);
                        const dayEnd = fromLocalDateTime(addDays(selectedDate, 1), '00:00', tenantTimezone);
                        const matchingTimeOff = timeOff.filter(off => (
                            off.practitionerId === practitionerId
                            && new Date(off.startUtc) < dayEnd
                            && new Date(off.endUtc) > dayStart
                        ));

                        if (matchingTimeOff.length === 0) return null;

                        return (
                            <>
                                <Divider sx={{ width: '100%' }} />
                                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                                    Time off
                                </Typography>
                                <Stack spacing={0.5} sx={{ width: '100%' }}>
                                    {matchingTimeOff.map(off => (
                                        <Box
                                            key={off.id}
                                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1 }}
                                        >
                                            <Typography variant="body2" sx={{ minWidth: 0 }}>
                                                {formatTimeLocal(new Date(off.startUtc) < dayStart ? dayStart : off.startUtc, tenantTimezone)}
                                                {' - '}
                                                {formatTimeLocal(new Date(off.endUtc) > dayEnd ? dayEnd : off.endUtc, tenantTimezone)}
                                                {off.reason ? ` · ${off.reason}` : ''}
                                            </Typography>
                                            <Button
                                                color="error"
                                                size="small"
                                                startIcon={<DeleteOutline />}
                                                onClick={() => {
                                                    setConfirmEditScope({ open: false, practitionerId: null, locationId: null, date: null });
                                                    setTimeOffDeleteConfirmation({ open: true, timeOff: off });
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </Box>
                                    ))}
                                </Stack>
                            </>
                        );
                    })()}
                    <Divider sx={{ width: '100%' }} />
                    <Can section="scheduling" action="send-appointment-text">
                        <Button
                            color="primary"
                            variant="outlined"
                            fullWidth

                            onClick={() => {
                                const { locationId: lid, date } = confirmEditScope;
                                setDayRemindersDialog({
                                    open: true,
                                    date: date,
                                    locationId: lid,
                                    locationName: `Location ${lid}` // Can be enhanced later with actual name lookup
                                });
                                setConfirmEditScope({ open: false, practitionerId: null, locationId: null, date: null });
                            }}
                            title="Send SMS reminders to all patients with appointments on this date"
                        >
                            Send SMS Reminders for This Day
                        </Button>
                    </Can>
                </DialogActions>
            </Dialog>

            {/* Month overflow popover */}
            <Popover
                open={Boolean(monthOverflow.anchorEl)}
                anchorEl={monthOverflow.anchorEl}
                onClose={() => setMonthOverflow({ anchorEl: null, date: null })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { minWidth: 260, maxWidth: 360, p: 1 } }}
            >
                <Box>
                    <Typography variant="subtitle2" sx={{ px: 1, py: 0.5 }}>Appointments</Typography>
                    <Divider sx={{ my: 0.5 }} />
                    <Stack spacing={0.5} sx={{ maxHeight: 320, overflowY: 'auto', px: 0.5 }}>
                        {(() => {
                            if (!monthOverflow.date) return null;
                            const d = monthOverflow.date;
                                const list = appointments
                                .filter(a => new Date(a.startUtc).getMonth() === d.getMonth() && sameDay(a.startUtc, d) && a.locationId === locationId && selectedPractitionerIds.includes(a.practitionerId))
                                .sort((a, b) => new Date(a.startUtc) - new Date(b.startUtc));
                            if (list.length === 0) return <Typography variant="caption" sx={{ color: 'text.secondary', px: 1 }}>No appointments</Typography>;
                            return list.map(a => {
                                const pc = practitionerColorMap.get(a.practitionerId) || { bg: 'rgba(25,118,210,0.08)', border: 'rgba(25,118,210,0.25)', dot: '#1976d2' };
                                const prName = practitionersMap.get(a.practitionerId)?.fullName || `#${a.practitionerId}`;
                                return (
                                    <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: `1px solid ${pc.border}`, bgcolor: pc.bg, borderRadius: 0.75, p: 0.8 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: pc.dot }} />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                                {formatTimeLocal(a.startUtc)} — {a.patientName || '(no name)'}{a.patientAge != null && ` (${a.patientAge})`}
                                            </Typography>
                                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                {prName}{a.notes ? ` • ${a.notes}` : ''}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            });
                        })()}
                    </Stack>
                    <Divider sx={{ my: 0.5 }} />
                    <Stack direction="row" justifyContent="flex-end" sx={{ px: 1, py: 0.5 }}>
                        <Button size="small" onClick={() => { if (monthOverflow.date) { setView('day'); setActiveDate(monthOverflow.date); } setMonthOverflow({ anchorEl: null, date: null }); }}>Open day</Button>
                    </Stack>
                </Box>
            </Popover>

            <DayOverrideDialog
                open={dayOverrideDialog.open}
                practitionerId={dayOverrideDialog.practitionerId}
                locationId={dayOverrideDialog.locationId}
                date={dayOverrideDialog.date || new Date()}
                baseSchedule={dayOverrideDialog.open ? getDaySchedule(dayOverrideDialog.date, dayOverrideDialog.practitionerId, dayOverrideDialog.locationId) : null}
                onSave={({ pid, lid, date, override }) => {
                    ScheduleService.setDayOverrideV2({ practitionerId: pid, locationId: lid, date, override })
                        .then(() => {
                            const key = `${pid}-${lid}-${date}`;
                            setDayOverrides(prev => {
                                const m = new Map(prev);
                                m.set(key, override);
                                return m;
                            });
                            showMsg('Schedule updated for this date', 'success');
                        })
                        .catch(() => {
                            // error toast handled by interceptor
                        })
                        .finally(() => {
                            setDayOverrideDialog({ open: false, practitionerId: null, locationId: null, date: null });
                        });
                }}
                onClose={() => setDayOverrideDialog({ open: false, practitionerId: null, locationId: null, date: null })}
            />

            {/* Practitioner Picker for multi-select safety */}
            <Dialog
                open={practitionerPicker.open}
                onClose={() => setPractitionerPicker({ open: false, intent: null, date: null })}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Select practitioner</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1 }}>Select a practitioner.</Typography>
                    <Stack spacing={0.5}>
                        {practitionersShown.map(p => (
                            <Button
                                key={p.id}
                                variant="outlined"
                                onClick={() => {
                                    const intent = practitionerPicker.intent;
                                    const date = practitionerPicker.date || activeDate;
                                    setPractitionerPicker({ open: false, intent: null, date: null });
                                    if (intent === 'day-override') {
                                        const base = getDaySchedule(date, p.id, locationId);
                                        if (!base || !base.enabled) { showMsg('No recurring schedule on this date', 'warning'); return; }
                                        setDayOverrideDialog({ open: true, practitionerId: p.id, locationId, date });
                                    } else if (intent === 'edit-schedule') {
                                        setConfirmEditScope({ open: true, practitionerId: p.id, locationId, date });
                                    } else {
                                        // 'series' or fallback: open full series editor
                                        setScheduleDialog({ open: true, practitionerId: p.id, locationId, mode: 'create' });
                                    }
                                }}
                            >
                                {p.name}
                            </Button>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPractitionerPicker({ open: false, intent: null, date: null })}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {isMobile && (
                <Popover
                    open={!!infoAnchor}
                    anchorEl={infoAnchor}
                    onClose={handleInfoClose}
                    anchorOrigin={{ vertical:'bottom', horizontal:'right' }}
                    transformOrigin={{ vertical:'top', horizontal:'right' }}
                    PaperProps={{ sx:{ p:1.2, borderRadius:2, boxShadow:3, maxWidth:260 } }}
                >
                    {infoAppt && (
                        <Stack spacing={0.75} sx={{ fontSize:12 }}>
                            <Typography variant="subtitle2" sx={{ lineHeight:1.1 }}>{infoAppt.patientName || '(no name)'}{infoAppt.patientAge != null && ` (${infoAppt.patientAge})`}</Typography>
                            <Typography variant="caption">{formatTimeLocal(infoAppt.startUtc)} - {formatTimeLocal(infoAppt.endUtc)}</Typography>
                            {infoAppt.patientEmail && <Typography variant="caption" sx={{ display:'block' }}>{infoAppt.patientEmail}</Typography>}
                            {infoAppt.patientMobile && <Typography variant="caption" sx={{ display:'block' }}>{infoAppt.patientMobile}</Typography>}
                            {infoAppt.notes && <Typography variant="caption" sx={{ display:'block', mt:0.5, color:'text.secondary' }}>{infoAppt.notes}</Typography>}
                            <Stack direction="row" spacing={1} sx={{ pt:0.5, flexWrap:'wrap' }}>
                                {infoAppt.patientEmail && (
                                    <Button size="small" variant="outlined" onClick={() => { window.location.href = `mailto:${infoAppt.patientEmail}`; }} sx={{ textTransform:'none' }}>Email</Button>
                                )}
                                {infoAppt.patientMobile && (
                                    <Button size="small" variant="outlined" onClick={() => { handleInfoClose(); setSendSmsDialog({ open: true, appointment: infoAppt }); }} sx={{ textTransform:'none' }}>Text</Button>
                                )}
                                {infoAppt.patientId && (
                                    <Button size="small" variant="contained" color="primary" onClick={() => { navigate(`/customers/${infoAppt.patientId}`); handleInfoClose(); }} sx={{ textTransform:'none' }}>Open Record</Button>
                                )}
                                <Button size="small" onClick={handleInfoClose} sx={{ textTransform:'none', ml:'auto' }}>Close</Button>
                            </Stack>
                        </Stack>
                    )}
                </Popover>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={2500} anchorOrigin={{vertical: 'top', horizontal: 'right'}}  onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>

            <SendSmsDialog
                open={sendSmsDialog.open}
                recipient={sendSmsDialog.appointment ? {
                    recipientId: sendSmsDialog.appointment.patientId,
                    phone: sendSmsDialog.appointment.patientMobile,
                    name: sendSmsDialog.appointment.patientName,
                    email: sendSmsDialog.appointment.patientEmail
                } : null}
                entityId={sendSmsDialog.appointment?.id}
                entityType="appointment"
                defaultCommunicationType="appointment_reminder"
                onClose={() => setSendSmsDialog({ open: false, appointment: null })}
                onSuccess={(message) => {
                    showMsg(message, 'success');
                    setSendSmsDialog({ open: false, appointment: null });
                }}
            />

            <SendDayRemindersDialog
                open={dayRemindersDialog.open}
                date={dayRemindersDialog.date}
                locationId={dayRemindersDialog.locationId}
                locationName={location?.name || ''}
                onClose={() => setDayRemindersDialog({ open: false, date: null, locationId: null, locationName: null })}
                onSuccess={(result) => {
                    // Optionally refresh appointments
                }}
            />
        </Box>
    );
}
